import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AdminHeader from '../components/AdminHeader';
import AdminTabs from '../components/AdminTabs';
import MarketingLinks from '../components/MarketingLinks';
import Toast from '../components/Toast';
import ConfirmationDialog from '../components/ConfirmationDialog';
import ContentInsertModal from '../components/ContentInsertModal';
import RevisionHistoryModal from '../components/RevisionHistoryModal';
import NoteContentEditorModal from '../components/NoteContentEditorModal';
import { parseTags, getAllTags, getCategoryColor, createStructuredTags } from '../utils/tagUtils';

import { API_BASE_URL } from '../config/api';

// Create axios instance with auth token
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper function to make authenticated requests
const authAxios = {
  get: (url, config = {}) => axios.get(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  put: (url, data, config = {}) => axios.put(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  post: (url, data, config = {}) => axios.post(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  delete: (url, config = {}) => axios.delete(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } })
};

function SettingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('email');

  // Test Email State
  const [emailTestForm, setEmailTestForm] = useState({ to: '', subject: 'Test Email from Design Shop', message: '' });
  const [emailTestLoading, setEmailTestLoading] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);

  const [activityReportForm, setActivityReportForm] = useState({
    enabled: false,
    intervalDays: 1,
    recipients: ''
  });
  const [activityReportSaving, setActivityReportSaving] = useState(false);
  const [activityReportSuccess, setActivityReportSuccess] = useState(null);
  const [activityReportError, setActivityReportError] = useState(null);
  const [activityReportTestLoading, setActivityReportTestLoading] = useState(false);
  const [activityReportTestResult, setActivityReportTestResult] = useState(null);

  // Config.json State
  const [configData, setConfigData] = useState(null);
  const [configContent, setConfigContent] = useState('');
  const [configLoading, setConfigLoading] = useState(true);
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState(null);
  const [configSuccess, setConfigSuccess] = useState(null);

  // Outreach Email Config State
  const [outreachConfig, setOutreachConfig] = useState({
    smtpHost: '',
    smtpPort: 587,
    smtpSecure: false,
    smtpUser: '',
    smtpPass: '',
    fromEmail: '',
    fromName: ''
  });
  const [outreachSaving, setOutreachSaving] = useState(false);
  const [outreachSuccess, setOutreachSuccess] = useState(null);

  // .env State
  const [envContent, setEnvContent] = useState('');
  const [envLoading, setEnvLoading] = useState(true);
  const [envSaving, setEnvSaving] = useState(false);
  const [envError, setEnvError] = useState(null);
  const [envSuccess, setEnvSuccess] = useState(null);

  // Promos State
  const [promos, setPromos] = useState([]);
  const [promosLoading, setPromosLoading] = useState(true);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [promoForm, setPromoForm] = useState({
    name: '',
    content: '',
    startDate: '',
    endDate: '',
    enabled: true
  });
  const [promoSaving, setPromoSaving] = useState(false);
  const [promoError, setPromoError] = useState(null);
  const [promoSuccess, setPromoSuccess] = useState(null);

  // Backup State
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupError, setBackupError] = useState(null);
  const [backupList, setBackupList] = useState([]);
  const [backupListLoading, setBackupListLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreError, setRestoreError] = useState(null);
  const [restoreSuccess, setRestoreSuccess] = useState(null);
  const [restoreConfirmDialog, setRestoreConfirmDialog] = useState({ open: false, filename: null, skipPreRestore: false, isUploaded: false, file: null });
  const [uploadedBackupFile, setUploadedBackupFile] = useState(null);
  const [uploadBackupLoading, setUploadBackupLoading] = useState(false);
  const [uploadBackupError, setUploadBackupError] = useState(null);
  const [deleteBackupLoading, setDeleteBackupLoading] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, filename: null });
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [selectedBackups, setSelectedBackups] = useState(new Set());
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkDownloadLoading, setBulkDownloadLoading] = useState(false);
  const [bulkDeleteConfirmDialog, setBulkDeleteConfirmDialog] = useState({ open: false });

  // Utilities State
  const [findDuplicatesLoading, setFindDuplicatesLoading] = useState(false);
  const [findDuplicatesResult, setFindDuplicatesResult] = useState(null);
  const [findDuplicatesError, setFindDuplicatesError] = useState(null);
  const [tagDesignsLoading, setTagDesignsLoading] = useState(false);
  const [tagDesignsResult, setTagDesignsResult] = useState(null);
  const [tagDesignsError, setTagDesignsError] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsFiles, setLogsFiles] = useState([]);
  const [logsError, setLogsError] = useState(null);
  const [selectedLogFile, setSelectedLogFile] = useState(null);
  const [logContent, setLogContent] = useState(null);
  const [logContentLoading, setLogContentLoading] = useState(false);
  const [logContentError, setLogContentError] = useState(null);
  const [deletingLogFile, setDeletingLogFile] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [liveLogView, setLiveLogView] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [liveLogError, setLiveLogError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const logContentRef = useRef(null); // Ref for auto-scroll
  // eslint-disable-next-line no-unused-vars
  const [logPollInterval, setLogPollInterval] = useState(0); // 0 = never, 5, 10, 30 seconds
  const [regenerateThumbnailsLoading, setRegenerateThumbnailsLoading] = useState(false);
  const [regenerateThumbnailsResult, setRegenerateThumbnailsResult] = useState(null);
  const [regenerateThumbnailsError, setRegenerateThumbnailsError] = useState(null);
  const [regenerateThumbnailsProgress, setRegenerateThumbnailsProgress] = useState(null);
  const [regenerateThumbnailsTaskId, setRegenerateThumbnailsTaskId] = useState(null);
  const [regenerateMockupsLoading, setRegenerateMockupsLoading] = useState(false);
  const [regenerateMockupsResult, setRegenerateMockupsResult] = useState(null);
  const [regenerateMockupsError, setRegenerateMockupsError] = useState(null);
  const [regenerateMockupsProgress, setRegenerateMockupsProgress] = useState(null);
  const [regenerateMockupsTaskId, setRegenerateMockupsTaskId] = useState(null);
  const [checkCacheLoading, setCheckCacheLoading] = useState(false);
  const [checkCacheResult, setCheckCacheResult] = useState(null);
  const [checkCacheError, setCheckCacheError] = useState(null);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [maintenanceError, setMaintenanceError] = useState(null);

  // Users State
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [showEditUserForm, setShowEditUserForm] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userFormData, setUserFormData] = useState({ username: '', password: '', role: 'user' });
  const [passwordData, setPasswordData] = useState({ password: '', confirmPassword: '' });
  const [userFormLoading, setUserFormLoading] = useState(false);

  // Bulk Tag Assignment State
  const [bulkTagDesigns, setBulkTagDesigns] = useState([]);
  const [bulkTagDesignsLoading, setBulkTagDesignsLoading] = useState(false);
  const [bulkTagDesignsError, setBulkTagDesignsError] = useState(null);
  const [selectedDesigns, setSelectedDesigns] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [tagFilter, setTagFilter] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [assignSuccess, setAssignSuccess] = useState(null);
  const [assignError, setAssignError] = useState(null);
  const [assignmentMode, setAssignmentMode] = useState('add');
  const [selectedMajorCategory, setSelectedMajorCategory] = useState('');
  const [newMinorTags, setNewMinorTags] = useState('');
  const [tagsToRemove, setTagsToRemove] = useState('');
  const [majorCategories, setMajorCategories] = useState([]);

  // JSON Editor State
  const [jsonEditorDesigns, setJsonEditorDesigns] = useState([]);
  const [jsonEditorLoading, setJsonEditorLoading] = useState(false);
  const [jsonEditorError, setJsonEditorError] = useState(null);
  const [selectedJsonDesign, setSelectedJsonDesign] = useState(null);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState(null);
  const [jsonSaving, setJsonSaving] = useState(false);
  const [jsonSearchQuery, setJsonSearchQuery] = useState('');
  const [jsonToast, setJsonToast] = useState(null);

  // Notes Editor State
  const [pages, setPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [pagesError, setPagesError] = useState(null);
  const [syncNotesIndexLoading, setSyncNotesIndexLoading] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [pageForm, setPageForm] = useState({
    title: '',
    slug: '',
    content: '',
    hideTitle: false,
    hideTags: false,
    type: 'note',
    tags: []
  });
  const [pageSaving, setPageSaving] = useState(false);
  const [pageDeleting, setPageDeleting] = useState(null);
  const [showDeletePageDialog, setShowDeletePageDialog] = useState(false);
  const [pageToast, setPageToast] = useState(null);
  const [pageTagsInput, setPageTagsInput] = useState(''); // Raw tags input string
  const [showContentInsertModal, setShowContentInsertModal] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [showNoteContentEditorModal, setShowNoteContentEditorModal] = useState(false);
  const pageContentTextareaRef = useRef(null);
  const [slugValidation, setSlugValidation] = useState({ checking: false, available: null, message: '' });
  // Notes filtering and sorting
  const [notesSearchQuery, setNotesSearchQuery] = useState('');
  const [notesTagFilter, setNotesTagFilter] = useState('');
  const [notesTypeFilter, setNotesTypeFilter] = useState(''); // '', 'note', 'block'
  const [notesSortBy, setNotesSortBy] = useState('updatedAt'); // 'createdAt', 'updatedAt', 'title', 'viewCount'

  // Categories State
  const [categoriesData, setCategoriesData] = useState(null);
  const [categoriesContent, setCategoriesContent] = useState('');
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesSaving, setCategoriesSaving] = useState(false);
  const [categoriesError, setCategoriesError] = useState(null);
  const [categoriesSuccess, setCategoriesSuccess] = useState(null);

  // Handle navigation state and URL params (e.g. from NoteDisplay or open-in-new-window link)
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    } else if (searchParams.get('activeTab')) {
      setActiveTab(searchParams.get('activeTab'));
    }
  }, [location.state, searchParams]);

  useEffect(() => {
    document.title = 'Settings & Utilities - Admin';
    fetchCategories();
    if (activeTab === 'configuration') {
      fetchConfig();
      fetchEnv();
    }
    if (activeTab === 'outreach-email' || activeTab === 'email') {
      fetchConfig();
    }
    if (activeTab === 'promos') {
      fetchPromos();
    }
    if (activeTab === 'logs') {
      fetchLogs();
    }
    if (activeTab === 'utilities') {
      fetchMaintenanceMode();
    }
    if (activeTab === 'tag-categories') {
      const categoriesResult = fetchCategories();
      // Handle both promise and direct result
      if (categoriesResult && typeof categoriesResult.then === 'function') {
        categoriesResult.then(categories => {
          if (categories && categories.categories) {
            setMajorCategories(categories.categories.map(c => c.name));
          }
        }).catch(err => {
          console.error('Error loading categories:', err);
          setMajorCategories([]);
        });
      } else if (categoriesResult && categoriesResult.categories) {
        // Direct result (cached)
        setMajorCategories(categoriesResult.categories.map(c => c.name));
      }
    }
    if (activeTab === 'bulk-tags') {
      fetchTagDesigns();
      const categoriesResult = fetchCategories();
      // Handle both promise and direct result
      if (categoriesResult && typeof categoriesResult.then === 'function') {
        categoriesResult.then(categories => {
          if (categories && categories.categories) {
            setMajorCategories(categories.categories.map(c => c.name));
          }
        }).catch(err => {
          console.error('Error loading categories:', err);
          setMajorCategories([]);
        });
      } else if (categoriesResult && categoriesResult.categories) {
        // Direct result (cached)
        setMajorCategories(categoriesResult.categories.map(c => c.name));
      }
    }
    if (activeTab === 'users') {
      fetchUsers();
    }
    if (activeTab === 'json-editor') {
      fetchJsonEditorDesigns();
    }
    if (activeTab === 'notes-editor') {
      fetchPages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  
  // Auto-select note when navigating from NoteDisplay or URL (e.g. open-in-new-window link)
  const editNoteIdentifier = location.state?.editNote ?? searchParams.get('editNote');
  useEffect(() => {
    if (activeTab === 'notes-editor' && editNoteIdentifier && pages.length > 0) {
      // Try to find by name first, then by slug as fallback
      let pageToEdit = pages.find(p => p.name === editNoteIdentifier);
      
      if (!pageToEdit) {
        pageToEdit = pages.find(p => p.slug === editNoteIdentifier);
      }
      
      if (pageToEdit && selectedPage?.name !== pageToEdit.name) {
        handleSelectPage(pageToEdit);
        // Clear state and URL params to prevent re-selecting on re-render
        navigate({ pathname: location.pathname, search: '' }, { replace: true, state: { activeTab: 'notes-editor' } });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, editNoteIdentifier, pages.length]);

  // Poll for thumbnail regeneration progress
  useEffect(() => {
    if (!regenerateThumbnailsTaskId) return;

    const pollProgress = async () => {
      try {
        const response = await authAxios.get(`${API_BASE_URL}/admin/utilities/regeneration-progress/${regenerateThumbnailsTaskId}`);
        if (response.data.success && response.data.data) {
          const progress = response.data.data;
          setRegenerateThumbnailsProgress(progress);
          
          // Stop polling if completed
          if (progress.status === 'completed') {
            setRegenerateThumbnailsResult({
              success: true,
              message: progress.message,
              data: {
                regeneratedCount: progress.generated,
                totalDesigns: progress.total,
                mockupCacheCleared: progress.mockupCacheCleared,
                errors: progress.errors.length > 0 ? progress.errors : undefined
              },
              background: false
            });
            setRegenerateThumbnailsTaskId(null);
            return;
          }
        }
      } catch (err) {
        console.error('Error polling thumbnail progress:', err);
      }
    };

    const interval = setInterval(pollProgress, 2000); // Poll every 2 seconds
    pollProgress(); // Initial poll

    return () => clearInterval(interval);
  }, [regenerateThumbnailsTaskId]);

  // Poll for mockup regeneration progress
  useEffect(() => {
    if (!regenerateMockupsTaskId) return;

    const pollProgress = async () => {
      try {
        const response = await authAxios.get(`${API_BASE_URL}/admin/utilities/regeneration-progress/${regenerateMockupsTaskId}`);
        if (response.data.success && response.data.data) {
          const progress = response.data.data;
          setRegenerateMockupsProgress(progress);
          
          // Stop polling if completed
          if (progress.status === 'completed') {
            setRegenerateMockupsResult({
              success: true,
              message: progress.message,
              data: {
                generatedCount: progress.generated,
                skippedCount: progress.skipped,
                errors: progress.errors.length > 0 ? progress.errors : undefined
              },
              background: false
            });
            setRegenerateMockupsTaskId(null);
            return;
          }
        }
      } catch (err) {
        console.error('Error polling mockup progress:', err);
      }
    };

    const interval = setInterval(pollProgress, 2000); // Poll every 2 seconds
    pollProgress(); // Initial poll

    return () => clearInterval(interval);
  }, [regenerateMockupsTaskId]);

  const fetchConfig = async () => {
    try {
      setConfigLoading(true);
      setConfigError(null);
      const response = await authAxios.get(`${API_BASE_URL}/admin/config`);
      if (response.data.success) {
        const data = response.data.data;
        setConfigData(data);
        setConfigContent(JSON.stringify(data, null, 2));
        const o = data?.outreach || {};
        setOutreachConfig({
          smtpHost: o.smtpHost || '',
          smtpPort: o.smtpPort ?? 587,
          smtpSecure: o.smtpSecure === true,
          smtpUser: o.smtpUser || '',
          smtpPass: '', // Never load password for security; leave blank to keep existing
          fromEmail: o.fromEmail || o.smtpUser || '',
          fromName: o.fromName || ''
        });
      } else {
        setConfigError('Failed to load config');
      }
    } catch (err) {
      console.error('Error fetching config:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('username');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('username');
        navigate('/login');
        return;
      }
      setConfigError(err.response?.data?.error || err.message || 'Failed to load config');
    } finally {
      setConfigLoading(false);
    }
  };

  const fetchEnv = async () => {
    try {
      setEnvLoading(true);
      setEnvError(null);
      const response = await authAxios.get(`${API_BASE_URL}/admin/env`);
      if (response.data.success) {
        setEnvContent(response.data.data || '');
      } else {
        setEnvError('Failed to load .env file');
      }
    } catch (err) {
      console.error('Error fetching .env:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('username');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('username');
        navigate('/login');
        return;
      }
      setEnvError(err.response?.data?.error || err.message || 'Failed to load .env file');
    } finally {
      setEnvLoading(false);
    }
  };

  const handleSendTestEmail = async (e) => {
    e.preventDefault();
    setEmailTestLoading(true);
    setEmailTestResult(null);

    try {
      const response = await authAxios.post(`${API_BASE_URL}/admin/test-email`, {
        to: emailTestForm.to,
        subject: emailTestForm.subject,
        message: emailTestForm.message
      });

      if (response.data.success) {
        setEmailTestResult({
          type: 'success',
          message: `Test email sent successfully to ${emailTestForm.to}! Message ID: ${response.data.messageId}`
        });
        setEmailTestForm({ to: '', subject: 'Test Email from Design Shop', message: '' });
        setTimeout(() => setEmailTestResult(null), 5000);
      } else {
        setEmailTestResult({
          type: 'error',
          message: response.data.error || 'Failed to send test email'
        });
      }
    } catch (err) {
      console.error('Error sending test email:', err);
      setEmailTestResult({
        type: 'error',
        message: err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to send test email'
      });
    } finally {
      setEmailTestLoading(false);
    }
  };

  useEffect(() => {
    if (!configData) return;
    const ar = configData.activityReport;
    if (ar && typeof ar === 'object') {
      const id = Number(ar.intervalDays);
      setActivityReportForm({
        enabled: ar.enabled === true,
        intervalDays: [1, 3, 4, 5, 7, 30].includes(id) ? id : 1,
        recipients: typeof ar.recipients === 'string' ? ar.recipients : ''
      });
    } else {
      setActivityReportForm({
        enabled: false,
        intervalDays: 1,
        recipients: ''
      });
    }
  }, [configData]);

  const handleSaveActivityReport = async () => {
    if (!configData) {
      setActivityReportError('Config not loaded. Please wait or reload.');
      return;
    }
    try {
      setActivityReportSaving(true);
      setActivityReportSuccess(null);
      setActivityReportError(null);
      const updatedConfig = {
        ...configData,
        activityReport: {
          enabled: activityReportForm.enabled,
          intervalDays: activityReportForm.intervalDays,
          recipients: activityReportForm.recipients.trim()
        }
      };
      const response = await authAxios.put(`${API_BASE_URL}/admin/config`, {
        data: updatedConfig
      });
      if (response.data.success) {
        setConfigData(updatedConfig);
        setConfigContent(JSON.stringify(updatedConfig, null, 2));
        setActivityReportSuccess('Activity report settings saved.');
        setTimeout(() => setActivityReportSuccess(null), 3000);
      } else {
        setActivityReportError(response.data.error || 'Failed to save');
      }
    } catch (err) {
      console.error('Error saving activity report settings:', err);
      setActivityReportError(err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setActivityReportSaving(false);
    }
  };

  const handleSendTestActivityReport = async () => {
    setActivityReportTestLoading(true);
    setActivityReportTestResult(null);
    try {
      const toParam = activityReportForm.recipients.trim();
      const response = await authAxios.post(
        `${API_BASE_URL}/admin/activity-report/test`,
        toParam ? { to: toParam } : {}
      );
      if (response.data.success) {
        setActivityReportTestResult({
          type: 'success',
          message: response.data.message || 'Test activity report sent. Check your inbox.'
        });
        setTimeout(() => setActivityReportTestResult(null), 8000);
      } else {
        setActivityReportTestResult({
          type: 'error',
          message: response.data.error || 'Failed to send'
        });
      }
    } catch (err) {
      setActivityReportTestResult({
        type: 'error',
        message: err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to send test report'
      });
    } finally {
      setActivityReportTestLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setConfigSaving(true);
      setConfigError(null);
      setConfigSuccess(null);

      // Validate JSON
      let parsedData;
      try {
        parsedData = JSON.parse(configContent);
      } catch (error) {
        setConfigError(`Invalid JSON: ${error.message}`);
        setConfigSaving(false);
        return;
      }

      const response = await authAxios.put(`${API_BASE_URL}/admin/config`, {
        data: parsedData
      });

      if (response.data.success) {
        setConfigSuccess('Config saved successfully!');
        setConfigData(parsedData);
        setTimeout(() => setConfigSuccess(null), 3000);
      } else {
        setConfigError(response.data.error || 'Failed to save config');
      }
    } catch (err) {
      console.error('Error saving config:', err);
      setConfigError(err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to save config');
    } finally {
      setConfigSaving(false);
    }
  };

  const handleSaveOutreachConfig = async () => {
    if (!configData) {
      setConfigError('Config not loaded. Please wait or reload.');
      return;
    }
    try {
      setOutreachSaving(true);
      setOutreachSuccess(null);
      setConfigError(null);

      const updatedConfig = {
        ...configData,
        outreach: {
          smtpHost: outreachConfig.smtpHost.trim(),
          smtpPort: parseInt(outreachConfig.smtpPort, 10) || 587,
          smtpSecure: outreachConfig.smtpSecure,
          smtpUser: outreachConfig.smtpUser.trim(),
          smtpPass: outreachConfig.smtpPass || (configData?.outreach?.smtpPass || ''),
          fromEmail: outreachConfig.fromEmail.trim() || outreachConfig.smtpUser.trim(),
          fromName: outreachConfig.fromName.trim() || 'Outreach'
        }
      };

      const response = await authAxios.put(`${API_BASE_URL}/admin/config`, {
        data: updatedConfig
      });

      if (response.data.success) {
        setConfigData(updatedConfig);
        setOutreachSuccess('Outreach email configuration saved!');
        setTimeout(() => setOutreachSuccess(null), 3000);
      } else {
        setConfigError(response.data.error || 'Failed to save');
      }
    } catch (err) {
      console.error('Error saving outreach config:', err);
      setConfigError(err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setOutreachSaving(false);
    }
  };

  const fetchPromos = async () => {
    try {
      setPromosLoading(true);
      setPromoError(null);
      const response = await authAxios.get(`${API_BASE_URL}/admin/promos`);
      if (response.data.success) {
        setPromos(response.data.data || []);
      } else {
        setPromoError('Failed to load promos');
      }
    } catch (err) {
      console.error('Error fetching promos:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('username');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('username');
        navigate('/login');
        return;
      }
      setPromoError(err.response?.data?.error || err.message || 'Failed to load promos');
    } finally {
      setPromosLoading(false);
    }
  };

  const handleSelectPromo = (promo) => {
    setSelectedPromo(promo);
    setPromoForm({
      name: promo.name || '',
      content: promo.content || '',
      startDate: promo.startDate ? promo.startDate.split('T')[0] : '',
      endDate: promo.endDate ? promo.endDate.split('T')[0] : '',
      enabled: promo.enabled !== false
    });
  };

  const handleNewPromo = () => {
    setSelectedPromo(null);
    setPromoForm({
      name: '',
      content: '',
      startDate: '',
      endDate: '',
      enabled: true
    });
  };

  const handleSavePromo = async () => {
    try {
      if (!promoForm.name || !promoForm.content) {
        setPromoError('Name and content are required');
        return;
      }

      setPromoSaving(true);
      setPromoError(null);
      setPromoSuccess(null);

      const promoData = {
        name: promoForm.name,
        content: promoForm.content,
        startDate: promoForm.startDate || null,
        endDate: promoForm.endDate || null,
        enabled: promoForm.enabled
      };

      let response;
      if (selectedPromo) {
        // Update existing
        response = await authAxios.put(`${API_BASE_URL}/admin/promos/${selectedPromo.id}`, promoData);
      } else {
        // Create new
        response = await authAxios.post(`${API_BASE_URL}/admin/promos`, promoData);
      }

      if (response.data.success) {
        setPromoSuccess(selectedPromo ? 'Promo updated successfully!' : 'Promo created successfully!');
        await fetchPromos();
        if (!selectedPromo) {
          handleNewPromo(); // Reset form
        }
        setTimeout(() => setPromoSuccess(null), 3000);
      } else {
        setPromoError(response.data.error || 'Failed to save promo');
      }
    } catch (err) {
      console.error('Error saving promo:', err);
      setPromoError(err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to save promo');
    } finally {
      setPromoSaving(false);
    }
  };

  const handleDeletePromo = async (promoId) => {
    if (!window.confirm('Are you sure you want to delete this promo?')) {
      return;
    }

    try {
      setPromoSaving(true);
      setPromoError(null);
      const response = await authAxios.delete(`${API_BASE_URL}/admin/promos/${promoId}`);
      if (response.data.success) {
        setPromoSuccess('Promo deleted successfully!');
        await fetchPromos();
        if (selectedPromo && selectedPromo.id === promoId) {
          handleNewPromo();
        }
        setTimeout(() => setPromoSuccess(null), 3000);
      } else {
        setPromoError(response.data.error || 'Failed to delete promo');
      }
    } catch (err) {
      console.error('Error deleting promo:', err);
      setPromoError(err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to delete promo');
    } finally {
      setPromoSaving(false);
    }
  };

  const handleSaveEnv = async () => {
    try {
      setEnvSaving(true);
      setEnvError(null);
      setEnvSuccess(null);

      const response = await authAxios.put(`${API_BASE_URL}/admin/env`, {
        content: envContent
      });

      if (response.data.success) {
        setEnvSuccess(response.data.message || '.env file saved successfully!');
        setTimeout(() => setEnvSuccess(null), 5000);
      } else {
        setEnvError(response.data.error || 'Failed to save .env file');
      }
    } catch (err) {
      console.error('Error saving .env:', err);
      setEnvError(err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to save .env file');
    } finally {
      setEnvSaving(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      setCategoriesError(null);
      const response = await authAxios.get(`${API_BASE_URL}/admin/categories`);
      if (response.data.success) {
        setCategoriesData(response.data.data);
        setCategoriesContent(JSON.stringify(response.data.data, null, 2));
      } else {
        setCategoriesError('Failed to load categories');
      }
    } catch (err) {
      // Handle authentication errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('username');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('username');
        navigate('/login');
        return;
      }
      console.error('Error fetching categories:', err);
      setCategoriesError(err.response?.data?.error || err.message || 'Failed to load categories');
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchMaintenanceMode = async () => {
    try {
      setMaintenanceError(null);
      const response = await authAxios.get(`${API_BASE_URL}/admin/config`);
      if (response.data.success) {
        const isEnabled = response.data.data?.maintenance?.enabled || false;
        setMaintenanceMode(isEnabled);
      }
    } catch (err) {
      console.error('Error fetching maintenance mode:', err);
      setMaintenanceError(err.response?.data?.error || err.message || 'Failed to load maintenance mode');
    }
  };

  // User Management Functions
  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      setUsersError(null);
      const response = await authAxios.get(`${API_BASE_URL}/admin/users`);
      if (response.data.success) {
        setUsers(response.data.data);
      } else {
        setUsersError('Failed to fetch users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setUsersError(err.response?.data?.error || 'Failed to fetch users');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!userFormData.username || !userFormData.password || !userFormData.role) {
      return;
    }

    try {
      setUserFormLoading(true);
      const response = await authAxios.post(`${API_BASE_URL}/admin/users`, userFormData);
      if (response.data.success) {
        setShowAddUserForm(false);
        setUserFormData({ username: '', password: '', role: 'user' });
        fetchUsers();
      }
    } catch (err) {
      console.error('Error creating user:', err);
    } finally {
      setUserFormLoading(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!userFormData.username || !userFormData.role) {
      return;
    }

    try {
      setUserFormLoading(true);
      const updateData = {
        username: userFormData.username,
        role: userFormData.role
      };
      if (userFormData.password) {
        updateData.password = userFormData.password;
      }
      const response = await authAxios.put(`${API_BASE_URL}/admin/users/${selectedUser.id}`, updateData);
      if (response.data.success) {
        setShowEditUserForm(false);
        setSelectedUser(null);
        setUserFormData({ username: '', password: '', role: 'user' });
        fetchUsers();
      }
    } catch (err) {
      console.error('Error updating user:', err);
    } finally {
      setUserFormLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!passwordData.password || !passwordData.confirmPassword) {
      return;
    }
    if (passwordData.password !== passwordData.confirmPassword) {
      return;
    }

    try {
      setUserFormLoading(true);
      const response = await authAxios.put(`${API_BASE_URL}/admin/users/${selectedUser.id}`, {
        password: passwordData.password
      });
      if (response.data.success) {
        setShowPasswordForm(false);
        setSelectedUser(null);
        setPasswordData({ password: '', confirmPassword: '' });
      }
    } catch (err) {
      console.error('Error changing password:', err);
    } finally {
      setUserFormLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      setUserFormLoading(true);
      setUsersError(null);
      const response = await authAxios.delete(`${API_BASE_URL}/admin/users/${selectedUser.id}`);
      if (response.data.success) {
        setShowDeleteUserDialog(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        setUsersError(response.data.error || 'Failed to delete user');
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setUsersError(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setUserFormLoading(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    return role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800';
  };

  // JSON Editor Functions
  const fetchJsonEditorDesigns = async () => {
    try {
      setJsonEditorLoading(true);
      setJsonEditorError(null);
      const response = await authAxios.get(`${API_BASE_URL}/admin/designs`);
      if (response.data.success) {
        setJsonEditorDesigns(response.data.data);
      } else {
        setJsonEditorError('Failed to load designs');
      }
    } catch (err) {
      console.error('Error fetching designs:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('username');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('username');
        navigate('/login');
        return;
      }
      setJsonEditorError(err.response?.data?.error || err.message || 'Failed to load designs');
    } finally {
      setJsonEditorLoading(false);
    }
  };

  useEffect(() => {
    if (selectedJsonDesign) {
      setJsonText(JSON.stringify(selectedJsonDesign, null, 2));
      setJsonError(null);
    } else {
      setJsonText('');
      setJsonError(null);
    }
  }, [selectedJsonDesign]);

  const handleSelectJsonDesign = (design) => {
    setSelectedJsonDesign(design);
    setJsonError(null);
  };

  const handleJsonChange = (e) => {
    const newText = e.target.value;
    setJsonText(newText);
    
    try {
      const parsed = JSON.parse(newText);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        setJsonError(null);
      } else {
        setJsonError('JSON must be an object');
      }
    } catch (err) {
      setJsonError(err.message);
    }
  };

  const handleJsonSave = async () => {
    if (!selectedJsonDesign) return;

    let parsedJson;
    try {
      parsedJson = JSON.parse(jsonText);
      if (!parsedJson || typeof parsedJson !== 'object' || Array.isArray(parsedJson)) {
        setJsonError('JSON must be an object');
        return;
      }
      if (!parsedJson.id || typeof parsedJson.id !== 'string') {
        setJsonError('Design ID is required and must be a string');
        return;
      }
    } catch (err) {
      setJsonError(err.message);
      return;
    }

    try {
      setJsonSaving(true);
      setJsonEditorError(null);
      setJsonError(null);

      const response = await authAxios.put(
        `${API_BASE_URL}/admin/designs/${selectedJsonDesign.id}/json`,
        parsedJson
      );

      if (response.data.success) {
        const newId = response.data.data.id;
        const oldId = selectedJsonDesign.id;
        
        if (newId !== oldId) {
          setJsonEditorDesigns(jsonEditorDesigns.filter(d => d.id !== oldId).concat(response.data.data));
          setSelectedJsonDesign(response.data.data);
          setJsonToast({ message: `Design saved successfully! ID changed from ${oldId} to ${newId}`, type: 'success' });
        } else {
          setJsonEditorDesigns(jsonEditorDesigns.map(d => 
            d.id === selectedJsonDesign.id ? response.data.data : d
          ));
          setSelectedJsonDesign(response.data.data);
          setJsonToast({ message: 'Design saved successfully!', type: 'success' });
        }
      } else {
        setJsonEditorError('Failed to save design');
        setJsonToast({ message: 'Failed to save design', type: 'error' });
      }
    } catch (err) {
      console.error('Error saving design:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('username');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('username');
        navigate('/login');
        return;
      }
      const errorMsg = err.response?.data?.error || err.message || 'Failed to save design';
      setJsonEditorError(errorMsg);
      setJsonToast({ message: errorMsg, type: 'error' });
    } finally {
      setJsonSaving(false);
    }
  };

  const handleJsonCancel = () => {
    setSelectedJsonDesign(null);
    setJsonText('');
    setJsonError(null);
  };

  // Notes Editor Functions
  const fetchPages = async () => {
    try {
      setPagesLoading(true);
      setPagesError(null);
      // Add type filter to query if specified
      const url = notesTypeFilter 
        ? `${API_BASE_URL}/admin/notes?type=${notesTypeFilter}`
        : `${API_BASE_URL}/admin/notes`;
      const response = await authAxios.get(url);
      if (response.data.success) {
        setPages(response.data.data);
      } else {
        setPagesError('Failed to load notes');
      }
    } catch (err) {
      console.error('Error fetching notes:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('username');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('username');
        navigate('/login');
        return;
      }
      setPagesError(err.response?.data?.error || err.message || 'Failed to load notes');
    } finally {
      setPagesLoading(false);
    }
  };
  
  // Refetch pages when type filter changes
  useEffect(() => {
    if (activeTab === 'notes-editor') {
      fetchPages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notesTypeFilter]);

  // Sync notes index with HTML files on disk (fixes orphaned pages after restore)
  const handleSyncNotesIndex = async () => {
    try {
      setSyncNotesIndexLoading(true);
      setPagesError(null);
      const response = await authAxios.post(`${API_BASE_URL}/admin/notes/sync-index`);
      if (response.data.success) {
        setPageToast({ message: response.data.message, type: 'success' });
        await fetchPages();
      } else {
        setPagesError(response.data.error || 'Failed to sync index');
      }
    } catch (err) {
      setPagesError(err.response?.data?.error || err.message || 'Failed to sync notes index');
    } finally {
      setSyncNotesIndexLoading(false);
    }
  };

  // Helper function to generate slug from title
  const generateSlug = (title) => {
    return title
      .trim()
      .toLowerCase()
      .replace(/[^a-zA-Z0-9\s-]/g, '') // Remove special characters except hyphens
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  };

  const handleSelectPage = (page) => {
    setSelectedPage(page);
    setPageForm({
      title: page.title || '',
      slug: page.slug || '',
      content: page.content || '',
      hideTitle: page.hideTitle || false,
      hideTags: page.hideTags || false,
      type: page.type || 'note',
      tags: page.tags || []
    });
    // Convert tags array to comma-separated string for the input field (already lowercase)
    setPageTagsInput(page.tags && page.tags.length > 0 ? page.tags.map(t => t.toLowerCase()).join(', ') : '');
  };

  const handlePageFormChange = (field, value) => {
    setPageForm(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      // Auto-generate slug when title changes (if slug is empty or matches old title's slug)
      if (field === 'title' && value) {
        const currentSlug = prev.slug || generateSlug(prev.title || '');
        const newSlug = generateSlug(value);
        // Only auto-update if slug was auto-generated (matches old title) or is empty
        if (!prev.slug || currentSlug === generateSlug(prev.title || '')) {
          updated.slug = newSlug;
        }
      }
      return updated;
    });
    
    // Reset slug validation when slug changes
    if (field === 'slug') {
      setSlugValidation({ checking: false, available: null, message: '' });
    }
  };

  // Check slug availability when slug changes
  useEffect(() => {
    const checkSlugAvailability = async () => {
      const slug = pageForm.slug?.trim();
      
      // Don't check if slug is empty or same as selected page's slug
      if (!slug || slug.length === 0) {
        setSlugValidation({ checking: false, available: null, message: '' });
        return;
      }
      
      // If editing existing page and slug hasn't changed, don't check
      if (selectedPage && selectedPage.slug === slug) {
        setSlugValidation({ checking: false, available: true, message: '' });
        return;
      }
      
      // Debounce the check
      const timeoutId = setTimeout(async () => {
        try {
          setSlugValidation({ checking: true, available: null, message: '' });
          const response = await authAxios.get(`${API_BASE_URL}/admin/notes/check-slug/${encodeURIComponent(slug)}`);
          
          if (response.data.success) {
            const available = response.data.available;
            setSlugValidation({
              checking: false,
              available: available,
              message: available 
                ? 'Slug is available' 
                : 'This slug is already in use'
            });
          } else {
            setSlugValidation({
              checking: false,
              available: false,
              message: response.data.error || 'Error checking slug'
            });
          }
        } catch (err) {
          // If it's a 404 or other error, assume slug check failed
          setSlugValidation({
            checking: false,
            available: false,
            message: 'Error checking slug availability'
          });
        }
      }, 500); // 500ms debounce
      
      return () => clearTimeout(timeoutId);
    };
    
    checkSlugAvailability();
  }, [pageForm.slug, selectedPage]);

  // Handle content insertion from modal
  const handleContentInsert = (newContent, cursorPosition) => {
    setPageForm(prev => ({
      ...prev,
      content: newContent
    }));
    
    // Set cursor position after state update
    setTimeout(() => {
      if (pageContentTextareaRef.current) {
        pageContentTextareaRef.current.focus();
        pageContentTextareaRef.current.setSelectionRange(cursorPosition, cursorPosition);
      }
    }, 0);
  };

  const handleCreatePage = async () => {
    if (!pageForm.title.trim()) {
      setPageToast({ message: 'Note title is required', type: 'error' });
      return;
    }

    // Parse tags from raw input and convert to lowercase
    const tags = pageTagsInput
      .split(',')
      .map(t => t.trim().toLowerCase())
      .filter(t => t.length > 0);

    try {
      setPageSaving(true);
      setPagesError(null);
      const response = await authAxios.post(`${API_BASE_URL}/admin/notes`, {
        ...pageForm,
        tags: tags,
        type: pageForm.type || 'note'
      });
      if (response.data.success) {
        setPageToast({ message: 'Note created successfully', type: 'success' });
        await fetchPages();
        setPageForm({ title: '', slug: '', content: '', hideTitle: false, hideTags: false, type: 'note', tags: [] });
        setPageTagsInput('');
        setSelectedPage(null);
      } else {
        setPagesError(response.data.error || 'Failed to create note');
        setPageToast({ message: response.data.error || 'Failed to create note', type: 'error' });
      }
    } catch (err) {
      console.error('Error creating page:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to create note';
      setPagesError(errorMsg);
      setPageToast({ message: errorMsg, type: 'error' });
    } finally {
      setPageSaving(false);
    }
  };

  const handleUpdatePage = async () => {
    if (!selectedPage) return;
    if (!pageForm.title.trim()) {
      setPageToast({ message: 'Note title is required', type: 'error' });
      return;
    }

    // Parse tags from raw input and convert to lowercase
    const tags = pageTagsInput
      .split(',')
      .map(t => t.trim().toLowerCase())
      .filter(t => t.length > 0);

    try {
      setPageSaving(true);
      setPagesError(null);
      const response = await authAxios.put(
        `${API_BASE_URL}/admin/notes/${selectedPage.name}`,
        {
          ...pageForm,
          tags: tags
        }
      );
      if (response.data.success) {
        setPageToast({ message: 'Note updated successfully', type: 'success' });
        await fetchPages();
        // Update selected page if name changed
        if (response.data.data.name !== selectedPage.name) {
          setSelectedPage(response.data.data);
        } else {
          setSelectedPage({ ...selectedPage, ...response.data.data });
        }
      } else {
        setPagesError(response.data.error || 'Failed to update note');
        setPageToast({ message: response.data.error || 'Failed to update note', type: 'error' });
      }
    } catch (err) {
      console.error('Error updating page:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to update note';
      setPagesError(errorMsg);
      setPageToast({ message: errorMsg, type: 'error' });
    } finally {
      setPageSaving(false);
    }
  };

  /** Save note content from full-screen editor (PUT to server, then sync form). Returns true on success. */
  const saveNoteContentFromModal = async (content) => {
    if (!selectedPage) return false;
    const tags = pageTagsInput
      .split(',')
      .map(t => t.trim().toLowerCase())
      .filter(t => t.length > 0);
    try {
      setPageSaving(true);
      setPagesError(null);
      const response = await authAxios.put(
        `${API_BASE_URL}/admin/notes/${selectedPage.name}`,
        { ...pageForm, content, tags }
      );
      if (response.data.success) {
        setPageToast({ message: 'Note saved successfully', type: 'success' });
        setPageForm((prev) => ({ ...prev, content }));
        await fetchPages();
        setSelectedPage((prev) => (prev ? { ...prev, ...response.data.data } : null));
        return true;
      }
      setPagesError(response.data.error || 'Failed to save note');
      setPageToast({ message: response.data.error || 'Failed to save note', type: 'error' });
      return false;
    } catch (err) {
      console.error('Error saving note from modal:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to save note';
      setPagesError(errorMsg);
      setPageToast({ message: errorMsg, type: 'error' });
      return false;
    } finally {
      setPageSaving(false);
    }
  };

  const handleDeletePage = async () => {
    if (!selectedPage) return;

    try {
      setPageDeleting(selectedPage.name);
      setPagesError(null);
      const response = await authAxios.delete(`${API_BASE_URL}/admin/notes/${selectedPage.name}`);
      if (response.data.success) {
        setPageToast({ message: 'Note deleted successfully', type: 'success' });
        await fetchPages();
        setSelectedPage(null);
        setPageForm({ title: '', slug: '', content: '', hideTitle: false, hideTags: false, type: 'note', tags: [] });
        setPageTagsInput('');
        setShowDeletePageDialog(false);
      } else {
        setPagesError(response.data.error || 'Failed to delete note');
        setPageToast({ message: response.data.error || 'Failed to delete note', type: 'error' });
      }
    } catch (err) {
      console.error('Error deleting page:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to delete note';
      setPagesError(errorMsg);
      setPageToast({ message: errorMsg, type: 'error' });
    } finally {
      setPageDeleting(null);
    }
  };

  const handleDuplicatePage = async () => {
    if (!selectedPage) return;

    try {
      setPageSaving(true);
      setPagesError(null);
      const response = await authAxios.post(`${API_BASE_URL}/admin/notes/${selectedPage.name}/duplicate`);
      if (response.data.success) {
        const duplicatedPage = response.data.data;
        setPageToast({ message: `Note duplicated successfully as "${duplicatedPage.title}"`, type: 'success' });
        await fetchPages();
        // Select the newly duplicated page
        const updatedPages = await authAxios.get(`${API_BASE_URL}/admin/notes`);
        if (updatedPages.data.success) {
          const newPage = updatedPages.data.data.find(p => p.name === duplicatedPage.name);
          if (newPage) {
            handleSelectPage(newPage);
          }
        }
      } else {
        setPagesError(response.data.error || 'Failed to duplicate note');
        setPageToast({ message: response.data.error || 'Failed to duplicate note', type: 'error' });
      }
    } catch (err) {
      console.error('Error duplicating page:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('username');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('username');
        navigate('/login');
        return;
      }
      const errorMsg = err.response?.data?.error || err.message || 'Failed to duplicate note';
      setPagesError(errorMsg);
      setPageToast({ message: errorMsg, type: 'error' });
    } finally {
      setPageSaving(false);
    }
  };

  const handleCancelPageEdit = () => {
    setSelectedPage(null);
    setPageForm({ title: '', slug: '', content: '', hideTitle: false, hideTags: false, type: 'note', tags: [] });
    setPageTagsInput('');
  };

  const handleJsonOpenInAddForm = () => {
    if (!selectedJsonDesign) return;

    let parsedJson;
    try {
      parsedJson = JSON.parse(jsonText);
      if (!parsedJson || typeof parsedJson !== 'object' || Array.isArray(parsedJson)) {
        setJsonError('JSON must be an object');
        setJsonToast({ message: 'Invalid JSON. Please fix errors before opening in Add Form.', type: 'error' });
        return;
      }
    } catch (err) {
      setJsonError(err.message);
      setJsonToast({ message: 'Invalid JSON. Please fix errors before opening in Add Form.', type: 'error' });
      return;
    }

    // Navigate to Add Design page with pre-filled data
    navigate('/admin/add-design', {
      state: { prefilledDesign: parsedJson }
    });
  };

  const handleJsonDuplicate = async () => {
    if (!selectedJsonDesign) return;

    let parsedJson;
    try {
      parsedJson = JSON.parse(jsonText);
      if (!parsedJson || typeof parsedJson !== 'object' || Array.isArray(parsedJson)) {
        setJsonError('JSON must be an object');
        setJsonToast({ message: 'Invalid JSON. Please fix errors before duplicating.', type: 'error' });
        return;
      }
    } catch (err) {
      setJsonError(err.message);
      setJsonToast({ message: 'Invalid JSON. Please fix errors before duplicating.', type: 'error' });
      return;
    }

    let newId = `${parsedJson.id}-copy`;
    let counter = 1;
    // eslint-disable-next-line no-loop-func
    while (jsonEditorDesigns.find(d => d.id === newId)) {
      counter++;
      newId = `${parsedJson.id}-copy${counter}`;
    }

    const duplicateDesign = {
      ...parsedJson,
      id: newId
    };

    if (duplicateDesign.title) {
      duplicateDesign.title = `${duplicateDesign.title} (Copy)`;
    }

    try {
      setJsonSaving(true);
      setJsonEditorError(null);
      setJsonError(null);

      const response = await authAxios.post(
        `${API_BASE_URL}/admin/designs`,
        duplicateDesign
      );

      if (response.data.success) {
        setJsonEditorDesigns([...jsonEditorDesigns, response.data.data]);
        setSelectedJsonDesign(response.data.data);
        setJsonText(JSON.stringify(response.data.data, null, 2));
        setJsonToast({ message: `Design duplicated successfully as ${newId}!`, type: 'success' });
      } else {
        const errorMsg = response.data.error || 'Failed to duplicate design';
        setJsonEditorError(errorMsg);
        setJsonToast({ message: errorMsg, type: 'error' });
      }
    } catch (err) {
      console.error('Error duplicating design:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('username');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('username');
        navigate('/login');
        return;
      }
      const errorMsg = err.response?.data?.error || err.message || 'Failed to duplicate design';
      setJsonEditorError(errorMsg);
      setJsonToast({ message: errorMsg, type: 'error' });
    } finally {
      setJsonSaving(false);
    }
  };

  const filteredJsonDesigns = useMemo(() => {
    if (!jsonSearchQuery.trim()) {
      return jsonEditorDesigns;
    }
    const query = jsonSearchQuery.toLowerCase();
    return jsonEditorDesigns.filter(design => {
      const designJson = JSON.stringify(design).toLowerCase();
      return designJson.includes(query);
    });
  }, [jsonEditorDesigns, jsonSearchQuery]);

  // Bulk Tag Assignment Functions
  const fetchTagDesigns = async () => {
    try {
      setBulkTagDesignsLoading(true);
      setBulkTagDesignsError(null);
      const response = await axios.get(`${API_BASE_URL}/designs`);
      if (response.data.success) {
        setBulkTagDesigns(response.data.data);
      } else {
        setBulkTagDesignsError('Failed to load designs');
      }
    } catch (err) {
      console.error('Error fetching designs:', err);
      setBulkTagDesignsError(err.response?.data?.error || err.message || 'Failed to load designs');
    } finally {
      setBulkTagDesignsLoading(false);
    }
  };

  // Filter designs based on search, category, and tag filters
  const filteredTagDesigns = useMemo(() => {
    return bulkTagDesigns.filter(design => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        // Convert tags to string if it's not already
        const tagsString = typeof design.tags === 'string' ? design.tags : (Array.isArray(design.tags) ? design.tags.join(' ') : String(design.tags || ''));
        const matchesSearch = 
          (design.id || '').toLowerCase().includes(query) ||
          (design.title || '').toLowerCase().includes(query) ||
          (design.description || '').toLowerCase().includes(query) ||
          tagsString.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Category filter
      if (categoryFilter) {
        // Convert tags to string if needed
        const tagsString = typeof design.tags === 'string' ? design.tags : (Array.isArray(design.tags) ? design.tags.join(' ') : String(design.tags || ''));
        const { majorCategory } = parseTags(tagsString);
        if (majorCategory !== categoryFilter) return false;
      }

      // Tag filter - only check minor tags (not major categories)
      if (tagFilter) {
        // Convert tags to string if needed
        const tagsString = typeof design.tags === 'string' ? design.tags : (Array.isArray(design.tags) ? design.tags.join(' ') : String(design.tags || ''));
        const { minorTags } = parseTags(tagsString);
        const tagFilterLower = tagFilter.toLowerCase();
        
        // Only check if tag matches any minor tag (exclude major categories)
        if (!minorTags.some(tag => tag.toLowerCase().includes(tagFilterLower))) {
          return false;
        }
      }

      return true;
    });
  }, [bulkTagDesigns, searchQuery, categoryFilter, tagFilter]);

  // Get all unique major categories from designs
  const availableCategories = useMemo(() => {
    const categories = new Set();
    bulkTagDesigns.forEach(design => {
      // Convert tags to string if needed
      const tagsString = typeof design.tags === 'string' ? design.tags : (Array.isArray(design.tags) ? design.tags.join(' ') : String(design.tags || ''));
      const { majorCategory } = parseTags(tagsString);
      if (majorCategory) {
        categories.add(majorCategory);
      }
    });
    return Array.from(categories).sort();
  }, [bulkTagDesigns]);

  // Get all unique minor tags from designs (exclude major categories)
  const availableTags = useMemo(() => {
    const tags = new Set();
    if (!majorCategories || majorCategories.length === 0) {
      // If categories not loaded yet, just return all tags
      bulkTagDesigns.forEach(design => {
        if (design.tags) {
          // Convert tags to string if needed
          const tagsString = typeof design.tags === 'string' ? design.tags : (Array.isArray(design.tags) ? design.tags.join(' ') : String(design.tags || ''));
          const { minorTags } = parseTags(tagsString);
          minorTags.forEach(tag => {
            if (tag) {
              tags.add(tag);
            }
          });
        }
      });
      return Array.from(tags).sort();
    }
    const majorCategoriesLower = majorCategories.map(c => c.toLowerCase());
    bulkTagDesigns.forEach(design => {
      if (design.tags) {
        // Convert tags to string if needed
        const tagsString = typeof design.tags === 'string' ? design.tags : (Array.isArray(design.tags) ? design.tags.join(' ') : String(design.tags || ''));
        const { minorTags } = parseTags(tagsString);
        minorTags.forEach(tag => {
          if (tag && !majorCategoriesLower.includes(tag.toLowerCase())) {
            tags.add(tag);
          }
        });
      }
    });
    return Array.from(tags).sort();
  }, [bulkTagDesigns, majorCategories]);

  const handleSelectDesign = (designId) => {
    const newSelected = new Set(selectedDesigns);
    if (newSelected.has(designId)) {
      newSelected.delete(designId);
    } else {
      newSelected.add(designId);
    }
    setSelectedDesigns(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedDesigns.size === filteredTagDesigns.length) {
      setSelectedDesigns(new Set());
    } else {
      setSelectedDesigns(new Set(filteredTagDesigns.map(d => d.id)));
    }
  };

  const handleAssignTags = async () => {
    if (selectedDesigns.size === 0) {
      setAssignError('Please select at least one design');
      return;
    }

    if (assignmentMode === 'replace' && !selectedMajorCategory) {
      setAssignError('Please select a major category when using replace mode');
      return;
    }

    if (assignmentMode === 'remove' && !tagsToRemove.trim()) {
      setAssignError('Please enter at least one tag to remove');
      return;
    }

    if (assignmentMode === 'add' && !newMinorTags.trim() && !selectedMajorCategory) {
      setAssignError('Please enter tags to add or select a major category');
      return;
    }

    setAssigning(true);
    setAssignError(null);
    setAssignSuccess(null);

    try {
      const selectedArray = Array.from(selectedDesigns);
      
      const results = await Promise.allSettled(
        selectedArray.map(async (designId) => {
          try {
            const design = bulkTagDesigns.find(d => d.id === designId);
            if (!design) {
              return { id: designId, success: false, error: 'Design not found' };
            }

            const { majorCategory: currentMajor, minorTags: currentMinor } = parseTags(design.tags || '');
            
            let newStructuredTags;
            
            const normalizeTags = (tags) => {
              return tags.map(t => t.trim().toLowerCase()).filter(t => t);
            };

            if (assignmentMode === 'remove') {
              const tagsToRemoveArray = normalizeTags(tagsToRemove.split(','));
              const allCurrentTags = getAllTags(design.tags || '').map(t => t.toLowerCase());
              const tagsToKeep = allCurrentTags.filter(tag => !tagsToRemoveArray.includes(tag));
              
              if (tagsToKeep.length === 0) {
                newStructuredTags = createStructuredTags('Abstract and Geometric', [], [], { autoGenerated: false, source: 'manual' });
              } else {
                const keptMajor = tagsToKeep.find(t => 
                  (majorCategories || []).map(c => c.toLowerCase()).includes(t)
                ) || (currentMajor && !tagsToRemoveArray.includes(currentMajor.toLowerCase()) 
                  ? currentMajor 
                  : tagsToKeep[0]);
                
                const keptMinor = tagsToKeep.filter(t => t !== keptMajor.toLowerCase());
                newStructuredTags = createStructuredTags(keptMajor, keptMinor, [], { autoGenerated: false, source: 'manual' });
              }
            } else if (assignmentMode === 'replace') {
              const minorTagsArray = normalizeTags(newMinorTags.split(','));
              newStructuredTags = createStructuredTags(
                selectedMajorCategory || null,
                minorTagsArray,
                [],
                { autoGenerated: false, source: 'manual' }
              );
            } else {
              const minorTagsArray = normalizeTags(newMinorTags.split(','));
              const existingMinor = (currentMinor || []).map(t => t.toLowerCase());
              const combinedMinor = [...new Set([...existingMinor, ...minorTagsArray])];
              const finalMajor = currentMajor || selectedMajorCategory || 'Abstract and Geometric';
              newStructuredTags = createStructuredTags(
                finalMajor,
                combinedMinor,
                [],
                { autoGenerated: false, source: 'manual' }
              );
            }

            const response = await authAxios.put(`${API_BASE_URL}/designs/${designId}`, {
              tags: newStructuredTags
            });

            return { id: designId, success: response.data.success };
          } catch (error) {
            return { 
              id: designId, 
              success: false, 
              error: error.response?.data?.error || error.message || 'Unknown error' 
            };
          }
        })
      );

      const successful = results.filter(r => 
        r.status === 'fulfilled' && r.value && r.value.success
      ).length;
      const failed = results.length - successful;

      if (failed === 0) {
        const actionText = assignmentMode === 'remove' ? 'removed tags from' : 'assigned tags to';
        setAssignSuccess(`Successfully ${actionText} ${successful} design(s)`);
        setSelectedDesigns(new Set());
        setNewMinorTags('');
        setTagsToRemove('');
        setSelectedMajorCategory('');
        await fetchTagDesigns();
      } else {
        setAssignError(`Updated ${successful} design(s), but ${failed} failed`);
        await fetchTagDesigns();
      }
    } catch (err) {
      console.error('Error assigning tags:', err);
      setAssignError(err.response?.data?.error || err.message || 'Failed to update tags');
    } finally {
      setAssigning(false);
    }
  };

  const toggleMaintenanceMode = async () => {
    try {
      setMaintenanceLoading(true);
      setMaintenanceError(null);
      
      // Get current config
      const response = await authAxios.get(`${API_BASE_URL}/admin/config`);
      if (!response.data.success) {
        throw new Error('Failed to load current config');
      }
      
      const currentConfig = response.data.data;
      const newMaintenanceMode = !maintenanceMode;
      
      // Update config with new maintenance mode
      const updatedConfig = {
        ...currentConfig,
        maintenance: {
          enabled: newMaintenanceMode
        }
      };
      
      // Save updated config
      const saveResponse = await authAxios.put(`${API_BASE_URL}/admin/config`, {
        data: updatedConfig
      });
      
      if (saveResponse.data.success) {
        setMaintenanceMode(newMaintenanceMode);
      } else {
        throw new Error(saveResponse.data.error || 'Failed to update maintenance mode');
      }
    } catch (err) {
      console.error('Error toggling maintenance mode:', err);
      setMaintenanceError(err.response?.data?.error || err.message || 'Failed to toggle maintenance mode');
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const handleSaveCategories = async () => {
    try {
      setCategoriesSaving(true);
      setCategoriesError(null);
      setCategoriesSuccess(null);

      // Validate JSON
      let parsedData;
      try {
        parsedData = JSON.parse(categoriesContent);
      } catch (error) {
        setCategoriesError(`Invalid JSON: ${error.message}`);
        setCategoriesSaving(false);
        return;
      }

      // Validate structure
      if (!parsedData.categories || !parsedData.defaultCategory) {
        setCategoriesError('Categories must include categories object and defaultCategory');
        setCategoriesSaving(false);
        return;
      }

      const response = await authAxios.put(`${API_BASE_URL}/admin/categories`, {
        data: parsedData
      });

      if (response.data.success) {
        setCategoriesSuccess('Categories saved successfully!');
        setCategoriesData(parsedData);
        // Clear categories cache so frontend reloads
        if (typeof window !== 'undefined' && window.clearCategoriesCache) {
          window.clearCategoriesCache();
        }
        setTimeout(() => setCategoriesSuccess(null), 3000);
      } else {
        setCategoriesError(response.data.error || 'Failed to save categories');
      }
    } catch (err) {
      console.error('Error saving categories:', err);
      setCategoriesError(err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to save categories');
    } finally {
      setCategoriesSaving(false);
    }
  };

  const fetchLogs = async () => {
    try {
      setLogsLoading(true);
      setLogsError(null);
      const response = await authAxios.get(`${API_BASE_URL}/admin/utilities/logs`);
      if (response.data.success) {
        setLogsFiles(response.data.data.files || []);
      } else {
        setLogsError(response.data.error || 'Failed to load log files');
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
      setLogsError(err.response?.data?.error || err.message || 'Failed to load log files');
    } finally {
      setLogsLoading(false);
    }
  };

  const fetchLogContent = async (filename, lines = 500) => {
    try {
      setLogContentLoading(true);
      setLogContentError(null);
      const response = await authAxios.get(`${API_BASE_URL}/admin/utilities/logs/${filename}?lines=${lines}`);
      if (response.data.success) {
        setLogContent(response.data.data);
        setSelectedLogFile(filename);
      } else {
        setLogContentError(response.data.error || 'Failed to load log content');
      }
    } catch (err) {
      console.error('Error fetching log content:', err);
      setLogContentError(err.response?.data?.error || err.message || 'Failed to load log content');
    } finally {
      setLogContentLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatLogLine = (line) => {
    if (!line || !line.trim()) return line;
    
    try {
      // Try to parse as JSON
      const logEntry = JSON.parse(line);
      
      // Extract timestamp, level, and message
      const timestamp = logEntry.timestamp || '';
      const level = logEntry.level || '';
      const message = logEntry.message || '';
      
      // Create a copy without timestamp, level, and message for the remaining JSON
      const { timestamp: _, level: __, message: ___, ...rest } = logEntry;
      const restJson = Object.keys(rest).length > 0 ? JSON.stringify(rest) : '';
      
      // Format: [timestamp] [level]: message {rest}
      if (timestamp && level && message) {
        const formatted = `[${timestamp}] [${level}]: ${message}${restJson ? ' ' + restJson : ''}`;
        return formatted;
      }
      
      // If it doesn't have the expected structure, return as-is
      return line;
    } catch (error) {
      // If it's not valid JSON, return the line as-is
      return line;
    }
  };

  const formatLogContent = (content) => {
    if (!content) return '';
    
    // Split by lines, reverse order (most recent first), and format each line
    const lines = content.split('\n').reverse();
    return lines.map(formatLogLine).join('\n');
  };

  const handleCreateBackup = async () => {
    try {
      setBackupLoading(true);
      setBackupError(null);

      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (!token) {
        setBackupError('Authentication required');
        return;
      }

      // Create a download link
      const response = await fetch(`${API_BASE_URL}/admin/backup`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.details || `HTTP error! status: ${response.status}`);
      }

      // Get the filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'designshop-backup.zip';
      if (contentDisposition) {
        // Try to match filename="..." or filename=...
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // If still default, generate timestamp on client side as fallback
      if (filename === 'designshop-backup.zip') {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const timestamp = `${year}${month}${day}${hours}${minutes}${seconds}`;
        filename = `designshop-backup-${timestamp}.zip`;
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error creating backup:', err);
      setBackupError(err.message || 'Failed to create backup');
    } finally {
      setBackupLoading(false);
    }
  };

  // Fetch backup list
  const fetchBackupList = async () => {
    try {
      setBackupListLoading(true);
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (!token) {
        return;
      }

      const response = await fetch(`${API_BASE_URL}/admin/backup/status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.details || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.data && data.data.backups) {
        setBackupList(data.data.backups.files || []);
      } else {
        // If response is successful but structure is unexpected, set empty list
        setBackupList([]);
      }
    } catch (err) {
      console.error('Error fetching backup list:', err);
      setBackupError(err.message || 'Failed to fetch backup list');
    } finally {
      setBackupListLoading(false);
    }
  };

  // Handle restore from uploaded backup
  const handleRestoreFromUpload = async (file, skipPreRestore = false) => {
    try {
      setUploadBackupLoading(true);
      setRestoreLoading(true);
      setUploadBackupError(null);
      setRestoreError(null);
      setRestoreSuccess(null);

      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (!token) {
        setUploadBackupError('Authentication required');
        return;
      }

      const formData = new FormData();
      formData.append('backupFile', file);
      formData.append('skipPreRestore', skipPreRestore);

      const response = await fetch(`${API_BASE_URL}/admin/backup/upload-restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.details || 'Failed to restore from uploaded backup');
      }

      let successMessage = `Restore completed successfully! ${data.data.restoredFiles} files and ${data.data.restoredDirs} directories restored.`;
      if (data.data.preRestoreBackup) {
        successMessage += ` A pre-restore backup was created: ${data.data.preRestoreBackup}`;
      }
      if (data.data.migrationsCount > 0) {
        const filesList = Object.keys(data.data.migrationsApplied).join(', ');
        successMessage += ` Note: ${data.data.migrationsCount} data migration(s) were automatically applied to ensure schema compatibility (${filesList}).`;
      }
      if (data.data.notesSynced > 0) {
        successMessage += ` ${data.data.notesSynced} note(s) were synced to the index.`;
      }
      successMessage += ' Refresh the page to see restored notes and other data.';
      setRestoreSuccess(successMessage);
      
      // Clear uploaded file
      setUploadedBackupFile(null);
      
      // Refresh backup list
      await fetchBackupList();
      
      // Close confirmation dialog
      setRestoreConfirmDialog({ open: false, filename: null, skipPreRestore: false, isUploaded: false, file: null });
    } catch (err) {
      console.error('Error restoring from uploaded backup:', err);
      setUploadBackupError(err.message || 'Failed to restore from uploaded backup');
      setRestoreError(err.message || 'Failed to restore from uploaded backup');
    } finally {
      setUploadBackupLoading(false);
      setRestoreLoading(false);
    }
  };

  // Handle restore
  const handleRestore = async (filename, skipPreRestore = false) => {
    try {
      setRestoreLoading(true);
      setRestoreError(null);
      setRestoreSuccess(null);

      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (!token) {
        setRestoreError('Authentication required');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/admin/backup/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ filename, skipPreRestore })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.details || 'Failed to restore backup');
      }

      let successMessage = `Restore completed successfully! ${data.data.restoredFiles} files and ${data.data.restoredDirs} directories restored.`;
      if (data.data.preRestoreBackup) {
        successMessage += ` A pre-restore backup was created: ${data.data.preRestoreBackup}`;
      }
      if (data.data.migrationsCount > 0) {
        const filesList = Object.keys(data.data.migrationsApplied).join(', ');
        successMessage += ` Note: ${data.data.migrationsCount} data migration(s) were automatically applied to ensure schema compatibility (${filesList}).`;
      }
      if (data.data.notesSynced > 0) {
        successMessage += ` ${data.data.notesSynced} note(s) were synced to the index.`;
      }
      successMessage += ' Refresh the page to see restored notes and other data.';
      setRestoreSuccess(successMessage);
      
      // Refresh backup list
      await fetchBackupList();
      
      // Close confirmation dialog
      setRestoreConfirmDialog({ open: false, filename: null, skipPreRestore: false, isUploaded: false, file: null });
    } catch (err) {
      console.error('Error restoring backup:', err);
      setRestoreError(err.message || 'Failed to restore backup');
    } finally {
      setRestoreLoading(false);
    }
  };

  // Handle download backup
  const handleDownloadBackup = async (filename) => {
    try {
      setDownloadLoading(true);
      setBackupError(null);

      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (!token) {
        setBackupError('Authentication required');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/admin/backup/download/${encodeURIComponent(filename)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || data.details || 'Failed to download backup');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading backup:', err);
      setBackupError(err.message || 'Failed to download backup');
    } finally {
      setDownloadLoading(false);
    }
  };

  // Handle delete backup
  const handleDeleteBackup = async (filename) => {
    try {
      setDeleteBackupLoading(true);
      setBackupError(null);

      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (!token) {
        setBackupError('Authentication required');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/admin/backup/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.details || 'Failed to delete backup');
      }

      setDeleteConfirmDialog({ open: false, filename: null });
      
      await fetchBackupList();
    } catch (err) {
      console.error('Error deleting backup:', err);
      setBackupError(err.message || 'Failed to delete backup');
    } finally {
      setDeleteBackupLoading(false);
    }
  };

  // Handle select/deselect single backup
  const handleSelectBackup = (filename) => {
    setSelectedBackups((prev) => {
      const next = new Set(prev);
      if (next.has(filename)) {
        next.delete(filename);
      } else {
        next.add(filename);
      }
      return next;
    });
  };

  // Handle select/deselect all backups
  const handleSelectAllBackups = () => {
    if (selectedBackups.size === backupList.length) {
      setSelectedBackups(new Set());
    } else {
      setSelectedBackups(new Set(backupList.map((b) => b.filename)));
    }
  };

  // Handle bulk download
  const handleBulkDownload = async () => {
    try {
      setBulkDownloadLoading(true);
      setBackupError(null);

      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (!token) {
        setBackupError('Authentication required');
        return;
      }

      const filenames = Array.from(selectedBackups);
      for (let i = 0; i < filenames.length; i++) {
        const filename = filenames[i];
        const response = await fetch(`${API_BASE_URL}/admin/backup/download/${encodeURIComponent(filename)}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || data.details || `Failed to download ${filename}`);
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        if (i < filenames.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }
    } catch (err) {
      console.error('Error downloading backups:', err);
      setBackupError(err.message || 'Failed to download selected backups');
    } finally {
      setBulkDownloadLoading(false);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    try {
      setBulkDeleteLoading(true);
      setBackupError(null);

      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (!token) {
        setBackupError('Authentication required');
        return;
      }

      const filenames = Array.from(selectedBackups);
      let errors = [];
      for (const filename of filenames) {
        try {
          const response = await fetch(`${API_BASE_URL}/admin/backup/${encodeURIComponent(filename)}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            const data = await response.json();
            errors.push(`${filename}: ${data.error || data.details || 'Failed to delete'}`);
          }
        } catch (err) {
          errors.push(`${filename}: ${err.message}`);
        }
      }

      setBulkDeleteConfirmDialog({ open: false });
      setSelectedBackups(new Set());
      
      if (errors.length > 0) {
        setBackupError(`Errors deleting backups: ${errors.join('; ')}`);
      }
      
      await fetchBackupList();
    } catch (err) {
      console.error('Error deleting backups:', err);
      setBackupError(err.message || 'Failed to delete selected backups');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  // Fetch backup list when backup tab is active
  useEffect(() => {
    if (activeTab === 'backup') {
      fetchBackupList();
    }
  }, [activeTab]);

  return (
    <div className="min-h-screen py-8 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <AdminHeader />

        <AdminTabs activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Test Email Tab */}
        {activeTab === 'email' && (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Test Email Configuration</h2>
            <p className="text-sm text-gray-600 mb-6">
              Send a test email to verify your SMTP configuration is working correctly.
            </p>
            
            <form onSubmit={handleSendTestEmail} className="space-y-4">
              <div>
                <label htmlFor="test-email-to" className="block text-sm font-medium text-gray-700 mb-1">
                  To Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  id="test-email-to"
                  type="email"
                  required
                  value={emailTestForm.to}
                  onChange={(e) => setEmailTestForm({ ...emailTestForm, to: e.target.value })}
                  placeholder="your-email@example.com"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label htmlFor="test-email-subject" className="block text-sm font-medium text-gray-700 mb-1">
                  Subject
                </label>
                <input
                  id="test-email-subject"
                  type="text"
                  value={emailTestForm.subject}
                  onChange={(e) => setEmailTestForm({ ...emailTestForm, subject: e.target.value })}
                  placeholder="Test Email from Design Shop"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label htmlFor="test-email-message" className="block text-sm font-medium text-gray-700 mb-1">
                  Custom Message (Optional)
                </label>
                <textarea
                  id="test-email-message"
                  value={emailTestForm.message}
                  onChange={(e) => setEmailTestForm({ ...emailTestForm, message: e.target.value })}
                  placeholder="Add a custom message to include in the test email..."
                  rows="3"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              {emailTestResult && (
                <div className={`p-3 rounded-md ${
                  emailTestResult.type === 'success' 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-red-50 border border-red-200'
                }`}>
                  <p className={`text-sm ${
                    emailTestResult.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {emailTestResult.message}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={emailTestLoading || !emailTestForm.to.trim()}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {emailTestLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </span>
                ) : (
                  'Send Test Email'
                )}
              </button>
            </form>

            <div className="mt-10 pt-8 border-t border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Scheduled activity report</h2>
              <p className="text-sm text-gray-600 mb-6">
                Email a daily table of visitor stats (UTC days). When the server has been off, the next send can include
                multiple days in one message. Uses the same SMTP settings as order emails (main <code className="text-xs bg-gray-100 px-1 rounded">.env</code>).
              </p>

              {configLoading ? (
                <p className="text-sm text-gray-500">Loading config…</p>
              ) : (
                <div className="space-y-4 max-w-xl">
                  {activityReportSuccess && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-sm text-green-800">{activityReportSuccess}</p>
                    </div>
                  )}
                  {activityReportError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-800">{activityReportError}</p>
                    </div>
                  )}

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={activityReportForm.enabled}
                      onChange={(e) =>
                        setActivityReportForm({ ...activityReportForm, enabled: e.target.checked })
                      }
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Enable scheduled reports</span>
                  </label>

                  <div>
                    <label htmlFor="activity-report-interval" className="block text-sm font-medium text-gray-700 mb-1">
                      Minimum days between emails
                    </label>
                    <select
                      id="activity-report-interval"
                      value={activityReportForm.intervalDays}
                      onChange={(e) =>
                        setActivityReportForm({
                          ...activityReportForm,
                          intervalDays: parseInt(e.target.value, 10)
                        })
                      }
                      className="w-full max-w-xs px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={1}>Every day (1)</option>
                      <option value={3}>Every 3 days</option>
                      <option value={4}>Every 4 days</option>
                      <option value={5}>Every 5 days</option>
                      <option value={7}>Every 7 days</option>
                      <option value={30}>Every 30 days</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="activity-report-recipients" className="block text-sm font-medium text-gray-700 mb-1">
                      Recipients (comma-separated)
                    </label>
                    <input
                      id="activity-report-recipients"
                      type="text"
                      value={activityReportForm.recipients}
                      onChange={(e) =>
                        setActivityReportForm({ ...activityReportForm, recipients: e.target.value })
                      }
                      placeholder="you@example.com, other@example.com"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex flex-wrap gap-3 pt-2">
                    <button
                      type="button"
                      onClick={handleSaveActivityReport}
                      disabled={activityReportSaving || configLoading}
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {activityReportSaving ? 'Saving…' : 'Save activity report settings'}
                    </button>
                    <button
                      type="button"
                      onClick={handleSendTestActivityReport}
                      disabled={activityReportTestLoading}
                      className="px-4 py-2 bg-gray-100 text-gray-800 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                      title="Sends yesterday (UTC) only; uses recipients above or saved config. Does not change the schedule cursor."
                    >
                      {activityReportTestLoading ? 'Sending…' : 'Send test report (yesterday UTC)'}
                    </button>
                  </div>

                  {activityReportTestResult && (
                    <div
                      className={`p-3 rounded-md ${
                        activityReportTestResult.type === 'success'
                          ? 'bg-green-50 border border-green-200'
                          : 'bg-red-50 border border-red-200'
                      }`}
                    >
                      <p
                        className={`text-sm ${
                          activityReportTestResult.type === 'success' ? 'text-green-800' : 'text-red-800'
                        }`}
                      >
                        {activityReportTestResult.message}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Outreach Email Configuration Tab */}
        {activeTab === 'outreach-email' && (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Outreach Email Configuration</h2>
            <p className="text-sm text-gray-600 mb-6">
              Configure a separate SMTP server and From address for outreach emails (e.g. cold outreach to contacts).
              This is independent from the main transactional email (orders, contact form, mailing list).
            </p>

            {configLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading config...</p>
              </div>
            ) : (
              <>
                {configError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-800">{configError}</p>
                  </div>
                )}
                {outreachSuccess && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-sm text-green-800">{outreachSuccess}</p>
                  </div>
                )}

                <div className="space-y-4 max-w-xl">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Host</label>
                    <input
                      type="text"
                      value={outreachConfig.smtpHost}
                      onChange={(e) => setOutreachConfig(c => ({ ...c, smtpHost: e.target.value }))}
                      placeholder="smtp.example.com"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Port</label>
                      <input
                        type="number"
                        value={outreachConfig.smtpPort}
                        onChange={(e) => setOutreachConfig(c => ({ ...c, smtpPort: e.target.value }))}
                        placeholder="587"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex items-center pt-8">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={outreachConfig.smtpSecure}
                          onChange={(e) => setOutreachConfig(c => ({ ...c, smtpSecure: e.target.checked }))}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700">Use TLS/SSL (secure)</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SMTP User</label>
                    <input
                      type="text"
                      value={outreachConfig.smtpUser}
                      onChange={(e) => setOutreachConfig(c => ({ ...c, smtpUser: e.target.value }))}
                      placeholder="outreach@yourdomain.com"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Password</label>
                    <input
                      type="password"
                      value={outreachConfig.smtpPass}
                      onChange={(e) => setOutreachConfig(c => ({ ...c, smtpPass: e.target.value }))}
                      placeholder={configData?.outreach?.smtpPass ? '•••••••• (leave blank to keep current)' : 'Enter password'}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">From Email</label>
                    <input
                      type="email"
                      value={outreachConfig.fromEmail}
                      onChange={(e) => setOutreachConfig(c => ({ ...c, fromEmail: e.target.value }))}
                      placeholder="outreach@yourdomain.com"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">From Name</label>
                    <input
                      type="text"
                      value={outreachConfig.fromName}
                      onChange={(e) => setOutreachConfig(c => ({ ...c, fromName: e.target.value }))}
                      placeholder="Doodling Jorge - Outreach"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <button
                    onClick={handleSaveOutreachConfig}
                    disabled={outreachSaving || !outreachConfig.smtpHost.trim() || !outreachConfig.smtpUser.trim()}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {outreachSaving ? 'Saving...' : 'Save Outreach Email Config'}
                  </button>
                  <p className="mt-2 text-xs text-gray-500">
                    Tip: You can also set OUTREACH_SMTP_PASS in .env to override the password (more secure for production).
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {/* Configuration Tab */}
        {activeTab === 'configuration' && (
          <div className="space-y-6">
            {/* config.json Editor */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">config.json Editor</h2>
                <button
                  onClick={fetchConfig}
                  disabled={configLoading}
                  className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
                >
                  {configLoading ? 'Loading...' : 'Reload'}
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Edit the configuration file. Changes take effect immediately.
              </p>

              {configLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-gray-600">Loading config...</p>
                </div>
              ) : (
                <>
                  {configError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-800">{configError}</p>
                    </div>
                  )}
                  {configSuccess && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-sm text-green-800">{configSuccess}</p>
                    </div>
                  )}
                  <textarea
                    value={configContent}
                    onChange={(e) => setConfigContent(e.target.value)}
                    className="w-full h-96 px-3 py-2 text-sm font-mono border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    spellCheck={false}
                  />
                  <div className="mt-4 flex gap-3">
                    <button
                      onClick={handleSaveConfig}
                      disabled={configSaving}
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {configSaving ? 'Saving...' : 'Save Config'}
                    </button>
                    <button
                      onClick={() => setConfigContent(JSON.stringify(configData, null, 2))}
                      disabled={configSaving || !configData}
                      className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-300 disabled:opacity-50 transition-colors"
                    >
                      Reset
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* .env File Editor */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">.env File Editor</h2>
                <button
                  onClick={fetchEnv}
                  disabled={envLoading}
                  className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
                >
                  {envLoading ? 'Loading...' : 'Reload'}
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Edit environment variables. <strong className="text-red-600">Server restart required</strong> for changes to take effect.
              </p>

              {envLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-gray-600">Loading .env file...</p>
                </div>
              ) : (
                <>
                  {envError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-800">{envError}</p>
                    </div>
                  )}
                  {envSuccess && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-sm text-green-800">{envSuccess}</p>
                    </div>
                  )}
                  <textarea
                    value={envContent}
                    onChange={(e) => setEnvContent(e.target.value)}
                    className="w-full h-96 px-3 py-2 text-sm font-mono border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    spellCheck={false}
                    placeholder="SMTP_HOST=smtp.example.com&#10;SMTP_USER=user@example.com&#10;SMTP_PASS=password&#10;..."
                  />
                  <div className="mt-4 flex gap-3">
                    <button
                      onClick={handleSaveEnv}
                      disabled={envSaving}
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {envSaving ? 'Saving...' : 'Save .env'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Promos Tab */}
        {activeTab === 'promos' && (
          <>
            <MarketingLinks />
            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Promo Sections Management</h2>
              <button
                onClick={handleNewPromo}
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors"
              >
                + New Promo
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Manage promotional sections that appear above "Choose a design and customize it for your print format" on the gallery page.
              You can set date ranges to automatically show/hide promos, and create multiple promos for different campaigns.
            </p>

            {promoError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800">{promoError}</p>
              </div>
            )}
            {promoSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-800">{promoSuccess}</p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Promo List */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Existing Promos</h3>
                {promosLoading ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-sm text-gray-600">Loading promos...</p>
                  </div>
                ) : promos.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4">No promos created yet. Click "New Promo" to create one.</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {promos.map((promo) => {
                      const now = new Date();
                      const startDate = promo.startDate ? new Date(promo.startDate) : null;
                      const endDate = promo.endDate ? new Date(promo.endDate) : null;
                      const isActive = promo.enabled && 
                        (!startDate || now >= startDate) && 
                        (!endDate || now <= endDate);
                      
                      return (
                        <div
                          key={promo.id}
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            selectedPromo?.id === promo.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => handleSelectPromo(promo)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium text-gray-900">{promo.name}</h4>
                                {!promo.enabled && (
                                  <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">Disabled</span>
                                )}
                                {promo.enabled && isActive && (
                                  <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">Active</span>
                                )}
                                {promo.enabled && !isActive && (
                                  <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">
                                    {startDate && now < startDate ? 'Scheduled' : 'Expired'}
                                  </span>
                                )}
                              </div>
                              {promo.startDate || promo.endDate ? (
                                <p className="text-xs text-gray-500">
                                  {promo.startDate ? new Date(promo.startDate).toLocaleDateString() : 'No start'} - {promo.endDate ? new Date(promo.endDate).toLocaleDateString() : 'No end'}
                                </p>
                              ) : (
                                <p className="text-xs text-gray-500">No date restrictions</p>
                              )}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePromo(promo.id);
                              }}
                              className="ml-2 px-2 py-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Right: Promo Editor */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {selectedPromo ? 'Edit Promo' : 'Create New Promo'}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={promoForm.name}
                      onChange={(e) => setPromoForm({ ...promoForm, name: e.target.value })}
                      placeholder="e.g., Summer Sale 2024"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      HTML Content <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={promoForm.content}
                      onChange={(e) => setPromoForm({ ...promoForm, content: e.target.value })}
                      className="w-full h-48 px-3 py-2 text-sm font-mono border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      spellCheck={false}
                      placeholder="<div class='bg-blue-50 p-4 rounded-lg mb-4'>&#10;  <p class='text-blue-800 font-semibold'>Special Promotion!</p>&#10;  <a href='/special' class='text-blue-600 underline'>Click here</a>&#10;</div>"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date (optional)
                      </label>
                      <input
                        type="date"
                        value={promoForm.startDate}
                        onChange={(e) => setPromoForm({ ...promoForm, startDate: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Date (optional)
                      </label>
                      <input
                        type="date"
                        value={promoForm.endDate}
                        onChange={(e) => setPromoForm({ ...promoForm, endDate: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="promo-enabled"
                      checked={promoForm.enabled}
                      onChange={(e) => setPromoForm({ ...promoForm, enabled: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="promo-enabled" className="ml-2 block text-sm text-gray-700">
                      Enabled
                    </label>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleSavePromo}
                      disabled={promoSaving || !promoForm.name || !promoForm.content}
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {promoSaving ? 'Saving...' : selectedPromo ? 'Update Promo' : 'Create Promo'}
                    </button>
                    {selectedPromo && (
                      <button
                        onClick={handleNewPromo}
                        className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-300 transition-colors"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          </>
        )}

        {/* Backup Tab */}
        {activeTab === 'backup' && (
          <div className="space-y-6">
            {/* Create Backup Section */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Create Backup</h2>
              <p className="text-sm text-gray-600 mb-6">
                Create a backup zip file containing all your data files.
              </p>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Files Included */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Files Included:</h3>
                  <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                    <li>Designs data (designs.json)</li>
                    <li>Formats data (formats.json)</li>
                    <li>Users data (users.json)</li>
                    <li>Orders data (orders.json)</li>
                    <li>Tokens data (tokens.json)</li>
                    <li>Download tokens data (download_tokens.json)</li>
                    <li>Digital packages metadata (digital_packages.json)</li>
                    <li>Categories data (categories.json)</li>
                    <li>Promos data (promos.json)</li>
                    <li>Redirects data (redirects.json)</li>
                    <li>Configuration (config.json)</li>
                    <li>Design images (assets/designs/)</li>
                    <li>Template images (assets/templates/)</li>
                    <li>Mockup images (assets/mockups/)</li>
                    <li>Static content files (assets/static_content/)</li>
                    <li>Notes/Pages content (pages/)</li>
                    <li>Visitor sessions (visitor_sessions/)</li>
                  </ul>
                </div>

                {/* Files Not Included */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Files Not Included:</h3>
                  <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                    <li>Digital package ZIP files (data/assets/digital_packages/) - too large</li>
                    <li>Design thumbnails cache (client/public/assets/) - can be regenerated</li>
                    <li>Mockup previews cache (server/data/cache/mockups/) - can be regenerated</li>
                    <li>Log files (server/logs/) - not needed for backup</li>
                  </ul>
                  <p className="text-xs text-gray-500 mt-3 italic">
                    Note: Cache files and thumbnails can be regenerated using the utilities, so they are not included to keep backup size manageable.
                  </p>
                </div>
              </div>

              {backupError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800">{backupError}</p>
                </div>
              )}

              <button
                onClick={handleCreateBackup}
                disabled={backupLoading}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {backupLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating Backup...
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download Backup
                  </>
                )}
              </button>
            </div>

            {/* Upload Backup Section */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Upload and Restore Backup</h2>
              <p className="text-sm text-gray-600 mb-6">
                Upload a backup ZIP file to restore from. This is useful for restoring backups that were downloaded or received from another source.
              </p>

              {uploadBackupError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800">{uploadBackupError}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Backup File (ZIP)
                  </label>
                  <input
                    type="file"
                    accept=".zip"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setUploadedBackupFile(file);
                        setUploadBackupError(null);
                      }
                    }}
                    disabled={uploadBackupLoading || restoreLoading}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                {uploadedBackupFile && (
                  <div className="p-3 bg-gray-50 rounded-md">
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Selected:</span> {uploadedBackupFile.name} ({(uploadedBackupFile.size / 1024 / 1024).toFixed(2)} MB)
                    </p>
                    <button
                      onClick={() => setUploadedBackupFile(null)}
                      disabled={uploadBackupLoading || restoreLoading}
                      className="mt-2 text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                    >
                      Clear selection
                    </button>
                  </div>
                )}

                {uploadedBackupFile && (
                  <button
                    onClick={() => setRestoreConfirmDialog({ 
                      open: true, 
                      filename: uploadedBackupFile.name, 
                      skipPreRestore: false,
                      isUploaded: true,
                      file: uploadedBackupFile
                    })}
                    disabled={uploadBackupLoading || restoreLoading}
                    className="px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Restore from Uploaded Backup
                  </button>
                )}
              </div>
            </div>

            {/* Backup Archives Section */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Backup Archives</h2>
              <p className="text-sm text-gray-600 mb-6">
                Manage your backup files. Download backups to your computer, delete old backups, or restore from a backup file. A pre-restore backup will be created automatically before restoring.
              </p>

              {restoreError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800">{restoreError}</p>
                </div>
              )}

              {restoreSuccess && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm text-green-800">{restoreSuccess}</p>
                </div>
              )}

              {backupListLoading ? (
                <div className="text-center py-8">
                  <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="text-sm text-gray-600 mt-2">Loading backups...</p>
                </div>
              ) : backupList.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">No backups available. Create a backup first.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedBackups.size > 0 && (
                    <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-md px-4 py-3">
                      <span className="text-sm font-medium text-blue-800">
                        {selectedBackups.size} backup{selectedBackups.size !== 1 ? 's' : ''} selected
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleBulkDownload}
                          disabled={bulkDownloadLoading || bulkDeleteLoading || restoreLoading || uploadBackupLoading || deleteBackupLoading}
                          className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                        >
                          {bulkDownloadLoading ? (
                            <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          )}
                          {bulkDownloadLoading ? 'Downloading...' : 'Download Selected'}
                        </button>
                        <button
                          onClick={() => setBulkDeleteConfirmDialog({ open: true })}
                          disabled={bulkDeleteLoading || bulkDownloadLoading || restoreLoading || uploadBackupLoading || deleteBackupLoading}
                          className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                        >
                          {bulkDeleteLoading ? (
                            <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          )}
                          {bulkDeleteLoading ? 'Deleting...' : 'Delete Selected'}
                        </button>
                        <button
                          onClick={() => setSelectedBackups(new Set())}
                          disabled={bulkDownloadLoading || bulkDeleteLoading}
                          className="px-3 py-1.5 bg-gray-200 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                            <input
                              type="checkbox"
                              checked={backupList.length > 0 && selectedBackups.size === backupList.length}
                              onChange={handleSelectAllBackups}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded cursor-pointer"
                            />
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Filename</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Age</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {backupList.map((backup, index) => (
                          <tr key={index} className={`hover:bg-gray-50 ${selectedBackups.has(backup.filename) ? 'bg-blue-50' : ''}`}>
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selectedBackups.has(backup.filename)}
                                onChange={() => handleSelectBackup(backup.filename)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded cursor-pointer"
                              />
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 font-mono">{backup.filename}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{backup.sizeMB} MB</td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {new Date(backup.created).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {backup.ageDays === 0 ? 'Today' : `${backup.ageDays} day${backup.ageDays !== 1 ? 's' : ''} ago`}
                            </td>
                            <td className="px-4 py-3 text-sm text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleDownloadBackup(backup.filename)}
                                  disabled={downloadLoading || restoreLoading || uploadBackupLoading || deleteBackupLoading || bulkDownloadLoading || bulkDeleteLoading}
                                  className="px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                                  title="Download backup"
                                >
                                  {downloadLoading ? (
                                    <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                  ) : (
                                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                  )}
                                  {downloadLoading ? 'Downloading...' : 'Download'}
                                </button>
                                <button
                                  onClick={() => setDeleteConfirmDialog({ open: true, filename: backup.filename })}
                                  disabled={restoreLoading || uploadBackupLoading || deleteBackupLoading || bulkDownloadLoading || bulkDeleteLoading}
                                  className="px-3 py-1 bg-red-600 text-white text-xs font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                                  title="Delete backup"
                                >
                                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Delete
                                </button>
                                <button
                                  onClick={() => setRestoreConfirmDialog({ open: true, filename: backup.filename, skipPreRestore: false, isUploaded: false, file: null })}
                                  disabled={restoreLoading || uploadBackupLoading || deleteBackupLoading || bulkDownloadLoading || bulkDeleteLoading}
                                  className="px-3 py-1 bg-orange-600 text-white text-xs font-medium rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                                  title="Restore from backup"
                                >
                                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                  Restore
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Restore Confirmation Dialog */}
            {restoreConfirmDialog.open && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Restore</h3>
                  <div className="text-gray-700 mb-6">
                    <p className="mb-2">Are you sure you want to restore from backup:</p>
                    <p className="font-mono text-sm bg-gray-100 p-2 rounded mb-2">{restoreConfirmDialog.filename}</p>
                    <p className="text-sm text-red-600 font-semibold mb-2">⚠️ Warning: This will overwrite all current data!</p>
                    <div className="mt-4 p-3 bg-gray-50 rounded-md">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={restoreConfirmDialog.skipPreRestore || false}
                          onChange={(e) => setRestoreConfirmDialog({ ...restoreConfirmDialog, skipPreRestore: e.target.checked })}
                          disabled={restoreLoading || uploadBackupLoading || deleteBackupLoading}
                          className="mt-1"
                        />
                        <div className="text-sm">
                          <span className="font-medium">Skip pre-restore backup</span>
                          <p className="text-xs text-gray-600 mt-1">
                            By default, a backup is created before restoring. Uncheck this if you want to skip creating a pre-restore backup.
                          </p>
                        </div>
                      </label>
                    </div>
                    {!restoreConfirmDialog.skipPreRestore && (
                      <p className="text-sm text-gray-600 mt-2">A pre-restore backup will be created automatically before restoring.</p>
                    )}
                  </div>
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => setRestoreConfirmDialog({ open: false, filename: null, skipPreRestore: false, isUploaded: false, file: null })}
                      disabled={restoreLoading || uploadBackupLoading || deleteBackupLoading}
                      className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        if (restoreConfirmDialog.isUploaded && restoreConfirmDialog.file) {
                          handleRestoreFromUpload(restoreConfirmDialog.file, restoreConfirmDialog.skipPreRestore);
                        } else {
                          handleRestore(restoreConfirmDialog.filename, restoreConfirmDialog.skipPreRestore);
                        }
                      }}
                      disabled={restoreLoading || uploadBackupLoading || deleteBackupLoading}
                      className="px-4 py-2 text-white rounded-lg transition-colors bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {(restoreLoading || uploadBackupLoading) ? 'Restoring...' : 'Restore'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Delete Confirmation Dialog */}
            {deleteConfirmDialog.open && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Delete</h3>
                  <p className="mb-2">Are you sure you want to delete this backup file?</p>
                  <p className="font-mono text-sm bg-gray-100 p-2 rounded mb-4">{deleteConfirmDialog.filename}</p>
                  <p className="text-sm text-red-600 mb-4">This action cannot be undone.</p>
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => setDeleteConfirmDialog({ open: false, filename: null })}
                      disabled={deleteBackupLoading}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleDeleteBackup(deleteConfirmDialog.filename)}
                      disabled={deleteBackupLoading}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {deleteBackupLoading ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Bulk Delete Confirmation Dialog */}
            {bulkDeleteConfirmDialog.open && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Bulk Delete</h3>
                  <p className="mb-2">Are you sure you want to delete <strong>{selectedBackups.size}</strong> backup{selectedBackups.size !== 1 ? 's' : ''}?</p>
                  <div className="bg-gray-100 rounded-md p-3 mb-4 max-h-40 overflow-y-auto">
                    {Array.from(selectedBackups).slice(0, 10).map((f, i) => (
                      <p key={i} className="font-mono text-xs text-gray-700 truncate">{f}</p>
                    ))}
                    {selectedBackups.size > 10 && (
                      <p className="text-xs text-gray-500 italic mt-1">...and {selectedBackups.size - 10} more</p>
                    )}
                  </div>
                  <p className="text-sm text-red-600 mb-4">This action cannot be undone.</p>
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => setBulkDeleteConfirmDialog({ open: false })}
                      disabled={bulkDeleteLoading}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      disabled={bulkDeleteLoading}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {bulkDeleteLoading ? 'Deleting...' : `Delete ${selectedBackups.size} Backup${selectedBackups.size !== 1 ? 's' : ''}`}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Utilities Tab */}
        {activeTab === 'utilities' && (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Data Utilities</h2>
            <p className="text-sm text-gray-600 mb-6">
              Utility tools for managing and maintaining your design data.
            </p>

            {/* Backup Data and Review Logs Links */}
            <div className="mb-6 flex flex-wrap gap-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <button
                  onClick={() => setActiveTab('backup')}
                  className="text-blue-700 hover:text-blue-900 font-medium underline flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Backup Data
                </button>
              </div>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <button
                  onClick={() => setActiveTab('logs')}
                  className="text-blue-700 hover:text-blue-900 font-medium underline flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Review Logs
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Find Duplicates */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Find Duplicate Design IDs</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Scan your designs.json file to identify any duplicate design IDs. This helps ensure data integrity.
                </p>

                {findDuplicatesError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-800">{findDuplicatesError}</p>
                  </div>
                )}

                {findDuplicatesResult && (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="mb-2">
                      <p className="text-sm font-medium text-blue-900">
                        Summary: {findDuplicatesResult.summary.duplicateCount} duplicate(s) found
                      </p>
                      <p className="text-xs text-blue-700 mt-1">
                        Total Designs: {findDuplicatesResult.summary.totalDesigns} | Unique IDs: {findDuplicatesResult.summary.uniqueIds}
                      </p>
                    </div>
                    {findDuplicatesResult.duplicates.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-blue-900 mb-2">Duplicate Details:</p>
                        <div className="max-h-60 overflow-y-auto space-y-2">
                          {findDuplicatesResult.duplicates.map((dup, idx) => (
                            <div key={idx} className="bg-white p-2 rounded border border-blue-100">
                              <p className="text-xs font-medium text-gray-900">ID: {dup.id}</p>
                              <p className="text-xs text-gray-600">Appears {dup.count} times at positions: {dup.positions.join(', ')}</p>
                              <div className="mt-1">
                                {dup.titles.map((title, tIdx) => (
                                  <p key={tIdx} className="text-xs text-gray-500 pl-2">• {title}</p>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {findDuplicatesResult.duplicates.length === 0 && (
                      <p className="text-sm text-blue-700">✅ No duplicates found! All design IDs are unique.</p>
                    )}
                  </div>
                )}

                <button
                  onClick={async () => {
                    setFindDuplicatesLoading(true);
                    setFindDuplicatesError(null);
                    setFindDuplicatesResult(null);
                    try {
                      const response = await authAxios.post(`${API_BASE_URL}/admin/utilities/find-duplicates`);
                      if (response.data.success) {
                        setFindDuplicatesResult(response.data.data);
                      } else {
                        setFindDuplicatesError(response.data.error || 'Failed to find duplicates');
                      }
                    } catch (err) {
                      console.error('Error finding duplicates:', err);
                      setFindDuplicatesError(err.response?.data?.error || err.message || 'Failed to find duplicates');
                    } finally {
                      setFindDuplicatesLoading(false);
                    }
                  }}
                  disabled={findDuplicatesLoading}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {findDuplicatesLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Scanning...
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Find Duplicates
                    </>
                  )}
                </button>
              </div>

              {/* Tag Designs */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Tag All Designs</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Automatically generate and assign tags to all designs based on their titles and descriptions. 
                  <strong className="text-blue-600"> Existing tags will be merged with new tags</strong> - your custom tags will be preserved!
                </p>

                {tagDesignsError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-800">{tagDesignsError}</p>
                  </div>
                )}

                {tagDesignsResult && (
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-sm font-medium text-green-900 mb-2">
                      ✅ {tagDesignsResult.message}
                    </p>
                    <p className="text-xs text-green-700 mb-3">
                      Total designs tagged: {tagDesignsResult.data.taggedCount}
                    </p>
                    {tagDesignsResult.data.sample && tagDesignsResult.data.sample.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-green-900 mb-2">Sample of tagged designs:</p>
                        <div className="max-h-60 overflow-y-auto space-y-1">
                          {tagDesignsResult.data.sample.map((item, idx) => (
                            <div key={idx} className="bg-white p-2 rounded border border-green-100">
                              <p className="text-xs font-medium text-gray-900">{item.title}</p>
                              <p className="text-xs text-gray-600">Tags: {item.tags || 'none'}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <button
                  onClick={async () => {
                    if (!window.confirm('This will merge new auto-generated tags with existing tags for all designs. Existing tags will be preserved. Continue?')) {
                      return;
                    }
                    setTagDesignsLoading(true);
                    setTagDesignsError(null);
                    setTagDesignsResult(null);
                    try {
                      const response = await authAxios.post(`${API_BASE_URL}/admin/utilities/tag-designs`);
                      if (response.data.success) {
                        setTagDesignsResult(response.data);
                        // Optionally refresh the page or show a success message
                      } else {
                        setTagDesignsError(response.data.error || 'Failed to tag designs');
                      }
                    } catch (err) {
                      console.error('Error tagging designs:', err);
                      setTagDesignsError(err.response?.data?.error || err.message || 'Failed to tag designs');
                    } finally {
                      setTagDesignsLoading(false);
                    }
                  }}
                  disabled={tagDesignsLoading}
                  className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {tagDesignsLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Tagging Designs...
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      Tag All Designs
                    </>
                  )}
                </button>
              </div>

              {/* Regenerate Thumbnails */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Regenerate Thumbnails</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Regenerate all design thumbnails and clear mockup preview cache. This is useful after updating design images or if thumbnails are missing. 
                  Existing thumbnails will be overwritten, and mockup previews will be regenerated on next view.
                </p>

                {regenerateThumbnailsError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-800">{regenerateThumbnailsError}</p>
                  </div>
                )}

                {regenerateThumbnailsResult && (
                  <div className={`mb-4 p-4 border rounded-md ${
                    regenerateThumbnailsResult.background 
                      ? 'bg-blue-50 border-blue-200' 
                      : 'bg-green-50 border-green-200'
                  }`}>
                    <p className={`text-sm font-medium mb-2 ${
                      regenerateThumbnailsResult.background 
                        ? 'text-blue-900' 
                        : 'text-green-900'
                    }`}>
                      {regenerateThumbnailsResult.background ? '⏳' : '✅'} {regenerateThumbnailsResult.message}
                    </p>
                    {regenerateThumbnailsResult.background && regenerateThumbnailsProgress ? (
                      <div className="mt-3">
                        <p className="text-sm font-semibold text-blue-900 mb-3 text-center">
                          Site Maintenance in Progress - please return in a few minutes
                        </p>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-blue-700 font-medium">
                            {regenerateThumbnailsProgress.message || `Processing ${regenerateThumbnailsProgress.current || 0}/${regenerateThumbnailsProgress.total || 0}...`}
                          </span>
                          <span className="text-xs text-blue-600">
                            {regenerateThumbnailsProgress.total > 0 
                              ? `${Math.round((regenerateThumbnailsProgress.current / regenerateThumbnailsProgress.total) * 100)}%`
                              : '0%'}
                          </span>
                        </div>
                        {regenerateThumbnailsProgress.total > 0 && (
                          <div className="w-full bg-blue-200 rounded-full h-2.5 mb-2">
                            <div 
                              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                              style={{ width: `${Math.min(100, (regenerateThumbnailsProgress.current / regenerateThumbnailsProgress.total) * 100)}%` }}
                            ></div>
                          </div>
                        )}
                        <p className="text-xs text-blue-600">
                          Generated: {regenerateThumbnailsProgress.generated || 0} | Errors: {regenerateThumbnailsProgress.errors?.length || 0}
                        </p>
                      </div>
                    ) : regenerateThumbnailsResult.background ? (
                      <div className="mt-2">
                        <p className="text-xs text-blue-700 mb-2">
                          The regeneration process is running in the background. This may take several minutes for large numbers of designs.
                        </p>
                        <p className="text-xs text-blue-600">
                          💡 Tip: Progress will appear here shortly...
                        </p>
                      </div>
                    ) : (
                      <>
                        <p className="text-xs text-green-700">
                          Total thumbnails regenerated: {regenerateThumbnailsResult.data?.regeneratedCount || 0}
                        </p>
                        {regenerateThumbnailsResult.data?.errors && regenerateThumbnailsResult.data.errors.length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-yellow-900 mb-2">Warnings:</p>
                            <div className="max-h-40 overflow-y-auto space-y-1">
                              {regenerateThumbnailsResult.data.errors.map((error, idx) => (
                                <p key={idx} className="text-xs text-yellow-700">• {error}</p>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                <button
                  onClick={async () => {
                    if (!window.confirm('This will regenerate all design thumbnails. Existing thumbnails will be overwritten. This process runs in the background and may take several minutes. Continue?')) {
                      return;
                    }
                    setRegenerateThumbnailsLoading(true);
                    setRegenerateThumbnailsError(null);
                    setRegenerateThumbnailsResult(null);
                    try {
                      const response = await authAxios.post(`${API_BASE_URL}/admin/utilities/regenerate-thumbnails`);
                      if (response.data.success) {
                        // Store task ID and start polling
                        setRegenerateThumbnailsTaskId(response.data.data?.taskId);
                        setRegenerateThumbnailsProgress({
                          status: 'processing',
                          current: 0,
                          total: 0,
                          generated: 0,
                          errors: [],
                          message: 'Starting...'
                        });
                        // Show immediate success message - processing continues in background
                        setRegenerateThumbnailsResult({
                          ...response.data,
                          message: response.data.message || 'Thumbnail regeneration started. Processing in background...',
                          background: true
                        });
                        setRegenerateThumbnailsLoading(false);
                      } else {
                        setRegenerateThumbnailsError(response.data.error || 'Failed to regenerate thumbnails');
                        setRegenerateThumbnailsLoading(false);
                      }
                    } catch (err) {
                      console.error('Error regenerating thumbnails:', err);
                      setRegenerateThumbnailsError(err.response?.data?.error || err.message || 'Failed to regenerate thumbnails');
                      setRegenerateThumbnailsLoading(false);
                    }
                  }}
                  disabled={regenerateThumbnailsLoading}
                  className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {regenerateThumbnailsLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Regenerating... (this may take a few minutes)
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Regenerate All Thumbnails
                    </>
                  )}
                </button>
              </div>

              {/* Regenerate Mockups */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Regenerate All Mockups</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Pre-generate and cache all mockup previews for all designs and formats. This improves user experience by eliminating wait times when viewing mockups. 
                  This process runs in the background and may take a long time depending on the number of designs and formats.
                </p>

                {regenerateMockupsError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-800">{regenerateMockupsError}</p>
                  </div>
                )}

                {regenerateMockupsResult && (
                  <div className={`mb-4 p-4 border rounded-md ${
                    regenerateMockupsResult.background 
                      ? 'bg-blue-50 border-blue-200' 
                      : 'bg-green-50 border-green-200'
                  }`}>
                    <p className={`text-sm font-medium mb-2 ${
                      regenerateMockupsResult.background 
                        ? 'text-blue-900' 
                        : 'text-green-900'
                    }`}>
                      {regenerateMockupsResult.background ? '⏳' : '✅'} {regenerateMockupsResult.message}
                    </p>
                    {regenerateMockupsResult.background && regenerateMockupsProgress ? (
                      <div className="mt-3">
                        <p className="text-sm font-semibold text-blue-900 mb-3 text-center">
                          Site Maintenance in Progress - please return in a few minutes
                        </p>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-blue-700 font-medium">
                            {regenerateMockupsProgress.message || `Processing ${regenerateMockupsProgress.current || 0}/${regenerateMockupsProgress.total || 0}...`}
                          </span>
                          <span className="text-xs text-blue-600">
                            {regenerateMockupsProgress.total > 0 
                              ? `${Math.round((regenerateMockupsProgress.current / regenerateMockupsProgress.total) * 100)}%`
                              : '0%'}
                          </span>
                        </div>
                        {regenerateMockupsProgress.total > 0 && (
                          <div className="w-full bg-blue-200 rounded-full h-2.5 mb-2">
                            <div 
                              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                              style={{ width: `${Math.min(100, (regenerateMockupsProgress.current / regenerateMockupsProgress.total) * 100)}%` }}
                            ></div>
                          </div>
                        )}
                        <p className="text-xs text-blue-600">
                          Generated: {regenerateMockupsProgress.generated || 0} | Skipped: {regenerateMockupsProgress.skipped || 0} | Errors: {regenerateMockupsProgress.errors?.length || 0}
                        </p>
                      </div>
                    ) : regenerateMockupsResult.background ? (
                      <div className="mt-2">
                        <p className="text-xs text-blue-700 mb-2">
                          The mockup generation process is running in the background. This may take a very long time (potentially hours) depending on the number of designs × formats × mockups.
                        </p>
                        <p className="text-xs text-blue-600">
                          💡 Tip: Progress will appear here shortly...
                        </p>
                      </div>
                    ) : (
                      <>
                        <p className="text-xs text-green-700">
                          Total mockups generated: {regenerateMockupsResult.data?.generatedCount || 0}
                        </p>
                        {regenerateMockupsResult.data?.errors && regenerateMockupsResult.data.errors.length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-yellow-900 mb-2">Warnings:</p>
                            <div className="max-h-40 overflow-y-auto space-y-1">
                              {regenerateMockupsResult.data.errors.map((error, idx) => (
                                <p key={idx} className="text-xs text-yellow-700">• {error}</p>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                <button
                  onClick={async () => {
                    if (!window.confirm('This will pre-generate all mockup previews for all designs and formats. This process may take a very long time (potentially hours) and runs in the background. Continue?')) {
                      return;
                    }
                    setRegenerateMockupsLoading(true);
                    setRegenerateMockupsError(null);
                    setRegenerateMockupsResult(null);
                    try {
                      const response = await authAxios.post(`${API_BASE_URL}/admin/utilities/regenerate-mockups`);
                      if (response.data.success) {
                        // Store task ID and start polling
                        setRegenerateMockupsTaskId(response.data.data?.taskId);
                        setRegenerateMockupsProgress({
                          status: 'processing',
                          current: 0,
                          total: 0,
                          generated: 0,
                          skipped: 0,
                          errors: [],
                          message: 'Starting...'
                        });
                        setRegenerateMockupsResult({
                          ...response.data,
                          message: response.data.message || 'Mockup regeneration started. Processing in background...',
                          background: true
                        });
                        setRegenerateMockupsLoading(false);
                      } else {
                        setRegenerateMockupsError(response.data.error || 'Failed to regenerate mockups');
                        setRegenerateMockupsLoading(false);
                      }
                    } catch (err) {
                      console.error('Error regenerating mockups:', err);
                      setRegenerateMockupsError(err.response?.data?.error || err.message || 'Failed to regenerate mockups');
                      setRegenerateMockupsLoading(false);
                    }
                  }}
                  disabled={regenerateMockupsLoading}
                  className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {regenerateMockupsLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Starting...
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Regenerate All Mockups
                    </>
                  )}
                </button>
              </div>

              {/* Check Cache Status */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Check Cache Status</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Verify which cached mockups are up to date and which are stale. Stale cache entries occur when format configurations (placements, offsets, etc.) change but the cache wasn't regenerated.
                </p>

                {checkCacheError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-800">{checkCacheError}</p>
                  </div>
                )}

                {checkCacheResult && (
                  <div className={`mb-4 p-4 border rounded-md ${
                    checkCacheResult.data?.stale > 0
                      ? 'bg-yellow-50 border-yellow-200' 
                      : 'bg-green-50 border-green-200'
                  }`}>
                    <div className="mb-2">
                      <p className={`text-sm font-medium ${
                        checkCacheResult.data?.stale > 0
                          ? 'text-yellow-900' 
                          : 'text-green-900'
                      }`}>
                        {checkCacheResult.data?.stale > 0 ? '⚠️' : '✅'} Cache Status Check Complete
                      </p>
                    </div>
                    <div className="text-sm space-y-1">
                      <p className="text-gray-700">
                        <strong>Total Checked:</strong> {checkCacheResult.data?.totalChecked || 0}
                      </p>
                      <p className="text-green-700">
                        <strong>Valid:</strong> {checkCacheResult.data?.valid || 0}
                      </p>
                      <p className={checkCacheResult.data?.stale > 0 ? 'text-yellow-700' : 'text-gray-700'}>
                        <strong>Stale:</strong> {checkCacheResult.data?.stale || 0}
                        {checkCacheResult.data?.stalePercentage && ` (${checkCacheResult.data?.stalePercentage}%)`}
                      </p>
                    </div>
                    {checkCacheResult.data?.stale > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-yellow-900 mb-2">
                          Stale entries found. Use "Regenerate All Mockups" to update them.
                        </p>
                        {checkCacheResult.data?.staleEntries && checkCacheResult.data.staleEntries.length > 0 && (
                          <div className="max-h-40 overflow-y-auto space-y-1">
                            <p className="text-xs font-medium text-yellow-900 mb-1">Sample stale entries (showing first 10):</p>
                            {checkCacheResult.data.staleEntries.slice(0, 10).map((entry, idx) => (
                              <p key={idx} className="text-xs text-yellow-700">
                                • {entry.designId} / {entry.formatId} (key: {entry.cacheKey.substring(0, 8)}...)
                              </p>
                            ))}
                            {checkCacheResult.data.staleEntries.length > 10 && (
                              <p className="text-xs text-yellow-600 italic">
                                ... and {checkCacheResult.data.staleEntries.length - 10} more
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <button
                  onClick={async () => {
                    setCheckCacheLoading(true);
                    setCheckCacheError(null);
                    setCheckCacheResult(null);
                    try {
                      const response = await authAxios.get(`${API_BASE_URL}/admin/utilities/check-cache`);
                      if (response.data.success) {
                        setCheckCacheResult(response.data);
                      } else {
                        setCheckCacheError(response.data.error || 'Failed to check cache');
                      }
                    } catch (err) {
                      console.error('Error checking cache:', err);
                      setCheckCacheError(err.response?.data?.error || err.message || 'Failed to check cache');
                    } finally {
                      setCheckCacheLoading(false);
                    }
                  }}
                  disabled={checkCacheLoading}
                  className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {checkCacheLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Checking...
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Check Cache Status
                    </>
                  )}
                </button>
              </div>

              {/* Maintenance Mode */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Maintenance Mode</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Enable maintenance mode to show a maintenance page to all visitors. Admin users can still access the admin interface. This is useful when performing updates or maintenance.
                </p>

                {maintenanceError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-800">{maintenanceError}</p>
                  </div>
                )}

                {maintenanceMode && (
                  <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm font-medium text-yellow-900 mb-1">
                      ⚠️ Maintenance mode is currently <strong>ENABLED</strong>
                    </p>
                    <p className="text-xs text-yellow-700">
                      Visitors will see the maintenance page. Admin users can still access the admin interface.
                    </p>
                  </div>
                )}

                {!maintenanceMode && (
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-sm font-medium text-green-900">
                      ✅ Maintenance mode is currently <strong>DISABLED</strong>
                    </p>
                  </div>
                )}

                <button
                  onClick={toggleMaintenanceMode}
                  disabled={maintenanceLoading}
                  className={`px-4 py-2 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 ${
                    maintenanceMode
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {maintenanceLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {maintenanceMode ? 'Disabling...' : 'Enabling...'}
                    </>
                  ) : (
                    <>
                      {maintenanceMode ? (
                        <>
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Disable Maintenance Mode
                        </>
                      ) : (
                        <>
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          Enable Maintenance Mode
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Navigation to Tag Management Pages */}
            <div className="mt-8 border-t border-gray-200 pt-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Tag Management</h2>
              <p className="text-sm text-gray-600 mb-6">
                Manage tag categories and perform bulk tag assignments on designs.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => setActiveTab('tag-categories')}
                  className="p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Tag Categories</h3>
                  <p className="text-sm text-gray-600">
                    Configure tag categories, keywords, and hierarchy. Changes affect how designs are automatically tagged.
                  </p>
                </button>
                <button
                  onClick={() => setActiveTab('bulk-tags')}
                  className="p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Bulk Tag Assignments</h3>
                  <p className="text-sm text-gray-600">
                    Select multiple designs and assign tags in bulk. Add, replace, or remove tags from multiple designs at once.
                  </p>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Review Logs Tab */}
        {activeTab === 'logs' && (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Review Logs</h2>
            <p className="text-sm text-gray-600 mb-6">
              View and review application log files. Logs are stored in JSON format and include all application events, errors, and debug information.
            </p>

            {logsError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800">{logsError}</p>
              </div>
            )}

            <div className="mb-4">
              <button
                onClick={fetchLogs}
                disabled={logsLoading}
                className="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {logsLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading...
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh Log Files
                  </>
                )}
              </button>
            </div>

            {logsFiles.length > 0 ? (
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Available Log Files:</p>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {logsFiles.map((file, idx) => (
                    <div
                      key={idx}
                      className={`p-3 border rounded-lg transition-colors ${
                        selectedLogFile === file.name
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div 
                          className="flex-1 cursor-pointer"
                          onClick={() => fetchLogContent(file.name)}
                        >
                          <p className="text-sm font-medium text-gray-900">{file.name}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatFileSize(file.size)} • {new Date(file.modified).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {selectedLogFile === file.name && logContentLoading && (
                            <svg className="animate-spin h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          )}
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!window.confirm(`Are you sure you want to delete "${file.name}"? This action cannot be undone.`)) {
                                return;
                              }
                              setDeletingLogFile(file.name);
                              try {
                                const response = await authAxios.delete(`${API_BASE_URL}/admin/utilities/logs/${file.name}`);
                                if (response.data.success) {
                                  // If the deleted file was being viewed, close it
                                  if (selectedLogFile === file.name) {
                                    setLogContent(null);
                                    setSelectedLogFile(null);
                                  }
                                  // Refresh the log files list
                                  await fetchLogs();
                                } else {
                                  setLogsError(response.data.error || 'Failed to delete log file');
                                }
                              } catch (err) {
                                console.error('Error deleting log file:', err);
                                setLogsError(err.response?.data?.error || err.message || 'Failed to delete log file');
                              } finally {
                                setDeletingLogFile(null);
                              }
                            }}
                            disabled={deletingLogFile === file.name || logsLoading}
                            className="px-2 py-1 text-xs text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                            title="Delete log file"
                          >
                            {deletingLogFile === file.name ? (
                              <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : !logsLoading && (
              <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                <p className="text-sm text-gray-600">
                  No log files found. Logs will appear here once the server starts logging.
                </p>
              </div>
            )}

            {logContentError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800">{logContentError}</p>
              </div>
            )}

            {logContent && (
              <div className="mt-4 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">{logContent.filename}</h4>
                    <p className="text-xs text-gray-500 mt-1">
                      Showing last {logContent.displayedLines} of {logContent.totalLines} lines • {formatFileSize(logContent.fileSize)}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setLogContent(null);
                      setSelectedLogFile(null);
                    }}
                    className="px-3 py-1 text-xs text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Close
                  </button>
                </div>
                <div className="bg-gray-900 text-gray-100 p-4 rounded-md overflow-auto max-h-96">
                  <pre className="text-xs font-mono whitespace-pre-wrap">{formatLogContent(logContent.content)}</pre>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tag Categories Tab */}
        {activeTab === 'tag-categories' && (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Tag Categories Configuration</h2>
            <p className="text-sm text-gray-600 mb-4">
              Configure tag categories, keywords, and hierarchy. Changes affect how designs are automatically tagged.
            </p>

            {categoriesLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading categories...</p>
              </div>
            ) : (
              <>
                {categoriesError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-800">{categoriesError}</p>
                  </div>
                )}
                {categoriesSuccess && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-sm text-green-800">{categoriesSuccess}</p>
                  </div>
                )}
                <textarea
                  value={categoriesContent}
                  onChange={(e) => setCategoriesContent(e.target.value)}
                  className="w-full h-96 px-3 py-2 text-sm font-mono border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  spellCheck={false}
                />
                <div className="mt-4 flex gap-3">
                  <button
                    onClick={handleSaveCategories}
                    disabled={categoriesSaving}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {categoriesSaving ? 'Saving...' : 'Save Categories'}
                  </button>
                  <button
                    onClick={() => setCategoriesContent(JSON.stringify(categoriesData, null, 2))}
                    disabled={categoriesSaving || !categoriesData}
                    className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-300 disabled:opacity-50 transition-colors"
                  >
                    Reset
                  </button>
                </div>
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> After saving, you MUST refresh the page or run "Tag All Designs" to apply changes to existing designs.
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">User Management</h2>
                <p className="text-sm text-gray-600 mt-1">Manage admin and user accounts</p>
              </div>
              <button
                onClick={() => {
                  setShowAddUserForm(true);
                  setUserFormData({ username: '', password: '', role: 'user' });
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Add User
              </button>
            </div>

            {usersLoading ? (
              <div className="text-center py-12">
                <div className="text-gray-500">Loading users...</div>
              </div>
            ) : usersError ? (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-red-800">{usersError}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Username
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {user.username}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setUserFormData({ username: user.username, password: '', role: user.role });
                              setShowEditUserForm(true);
                            }}
                            className="text-blue-600 hover:text-blue-900 mr-4"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setPasswordData({ password: '', confirmPassword: '' });
                              setShowPasswordForm(true);
                            }}
                            className="text-green-600 hover:text-green-900 mr-4"
                          >
                            Change Password
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowDeleteUserDialog(true);
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {users.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No users found
                  </div>
                )}
              </div>
            )}

            {/* Add User Form */}
            {showAddUserForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <h3 className="text-xl font-bold mb-4">Add New User</h3>
                  <form onSubmit={handleAddUser}>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Username
                      </label>
                      <input
                        type="text"
                        value={userFormData.username}
                        onChange={(e) => setUserFormData({ ...userFormData, username: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Password
                      </label>
                      <input
                        type="password"
                        value={userFormData.password}
                        onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Role
                      </label>
                      <select
                        value={userFormData.role}
                        onChange={(e) => setUserFormData({ ...userFormData, role: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddUserForm(false);
                          setUserFormData({ username: '', password: '', role: 'user' });
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={userFormLoading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        {userFormLoading ? 'Creating...' : 'Create User'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Edit User Form */}
            {showEditUserForm && selectedUser && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <h3 className="text-xl font-bold mb-4">Edit User</h3>
                  <form onSubmit={handleEditUser}>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Username
                      </label>
                      <input
                        type="text"
                        value={userFormData.username}
                        onChange={(e) => setUserFormData({ ...userFormData, username: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Password (leave blank to keep current)
                      </label>
                      <input
                        type="password"
                        value={userFormData.password}
                        onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="Leave blank to keep current password"
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Role
                      </label>
                      <select
                        value={userFormData.role}
                        onChange={(e) => setUserFormData({ ...userFormData, role: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowEditUserForm(false);
                          setSelectedUser(null);
                          setUserFormData({ username: '', password: '', role: 'user' });
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={userFormLoading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        {userFormLoading ? 'Updating...' : 'Update User'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Change Password Form */}
            {showPasswordForm && selectedUser && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <h3 className="text-xl font-bold mb-4">Change Password for {selectedUser.username}</h3>
                  <form onSubmit={handleChangePassword}>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        New Password
                      </label>
                      <input
                        type="password"
                        value={passwordData.password}
                        onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Confirm Password
                      </label>
                      <input
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        required
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowPasswordForm(false);
                          setSelectedUser(null);
                          setPasswordData({ password: '', confirmPassword: '' });
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={userFormLoading}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                      >
                        {userFormLoading ? 'Changing...' : 'Change Password'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Delete Confirmation Dialog */}
            {showDeleteUserDialog && selectedUser && (
              <ConfirmationDialog
                isOpen={showDeleteUserDialog}
                title="Delete User"
                message={`Are you sure you want to delete user "${selectedUser.username}"? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={handleDeleteUser}
                onCancel={() => {
                  setShowDeleteUserDialog(false);
                  setSelectedUser(null);
                }}
                loading={userFormLoading}
              />
            )}
          </div>
        )}

        {/* Bulk Tag Assignments Tab */}
        {activeTab === 'bulk-tags' && (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Bulk Tag Assignments</h2>
              <p className="text-sm text-gray-600 mt-1">Select multiple designs and assign tags in bulk</p>
            </div>

            {bulkTagDesignsLoading ? (
              <div className="text-center py-12">
                <div className="text-gray-500">Loading designs...</div>
              </div>
            ) : bulkTagDesignsError ? (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-red-800">{bulkTagDesignsError}</p>
              </div>
            ) : (
              <>
                {/* Search and Filters */}
                <div className="mb-6 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                  <div className="relative flex-1 w-full sm:max-w-md">
                    <input
                      type="text"
                      placeholder="Search designs..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <svg
                      className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <select
                    value={categoryFilter || ''}
                    onChange={(e) => setCategoryFilter(e.target.value || null)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Categories</option>
                    {(availableCategories || []).map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <select
                    value={tagFilter || ''}
                    onChange={(e) => setTagFilter(e.target.value || null)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Tags</option>
                    {(availableTags || []).map(tag => (
                      <option key={tag} value={tag}>{tag}</option>
                    ))}
                  </select>
                </div>

                {/* Tag Assignment Form */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-semibold mb-4">Assign Tags to Selected Designs</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Assignment Mode
                      </label>
                      <select
                        value={assignmentMode}
                        onChange={(e) => {
                          setAssignmentMode(e.target.value);
                          if (e.target.value === 'remove') {
                            setNewMinorTags('');
                            setSelectedMajorCategory('');
                          } else if (e.target.value !== 'remove') {
                            setTagsToRemove('');
                          }
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="add">Add Tags (Keep existing, add new)</option>
                        <option value="replace">Replace Tags (Replace all tags)</option>
                        <option value="remove">Remove Tags (Remove specified tags)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Major Category {assignmentMode === 'replace' && <span className="text-red-500">*</span>}
                      </label>
                      <select
                        value={selectedMajorCategory}
                        onChange={(e) => setSelectedMajorCategory(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">{assignmentMode === 'replace' ? 'Select Category' : 'Optional (keep existing if not set)'}</option>
                        {(majorCategories || []).map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {assignmentMode === 'remove' ? (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tags to Remove (comma-separated) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={tagsToRemove}
                        onChange={(e) => setTagsToRemove(e.target.value)}
                        placeholder="e.g., butterfly, insects, abstract and geometric"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Enter tags to remove separated by commas. Can include major categories or minor tags.
                      </p>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Minor Tags (comma-separated)
                      </label>
                      <input
                        type="text"
                        value={newMinorTags}
                        onChange={(e) => setNewMinorTags(e.target.value)}
                        placeholder="e.g., butterfly, insects, nature"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Enter tags separated by commas. In "Add" mode, these will be added to existing tags.
                      </p>
                    </div>
                  )}
                  <div className="flex items-center gap-4">
                    <button
                      onClick={handleAssignTags}
                      disabled={assigning || selectedDesigns.size === 0 || (assignmentMode === 'remove' && !tagsToRemove.trim())}
                      className={`px-6 py-2 text-white rounded-lg font-medium transition-colors ${
                        assignmentMode === 'remove' 
                          ? 'bg-red-600 hover:bg-red-700' 
                          : 'bg-blue-600 hover:bg-blue-700'
                      } disabled:bg-gray-400 disabled:cursor-not-allowed`}
                    >
                      {assigning 
                        ? (assignmentMode === 'remove' ? 'Removing...' : 'Assigning...') 
                        : assignmentMode === 'remove'
                          ? `Remove Tags from ${selectedDesigns.size} Selected Design(s)`
                          : `Assign Tags to ${selectedDesigns.size} Selected Design(s)`
                      }
                    </button>
                    {selectedDesigns.size > 0 && (
                      <button
                        onClick={() => setSelectedDesigns(new Set())}
                        className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium"
                      >
                        Clear Selection
                      </button>
                    )}
                  </div>
                  {assignSuccess && (
                    <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                      {assignSuccess}
                    </div>
                  )}
                  {assignError && (
                    <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                      {assignError}
                    </div>
                  )}
                </div>

                {/* Designs List */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <input
                              type="checkbox"
                              checked={selectedDesigns.size === filteredTagDesigns.length && filteredTagDesigns.length > 0}
                              onChange={handleSelectAll}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Design
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Major Category
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tags
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredTagDesigns.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                              No designs found matching your filters.
                            </td>
                          </tr>
                        ) : (
                          filteredTagDesigns.map((design) => {
                            const { majorCategory, minorTags } = parseTags(design.tags || '');
                            const isSelected = selectedDesigns.has(design.id);
                            const categoryColor = getCategoryColor(majorCategory);

                            return (
                              <tr
                                key={design.id}
                                className={isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}
                              >
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleSelectDesign(design.id)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center">
                                    {design.thumbnail_url && (
                                      <img
                                        src={design.thumbnail_url}
                                        alt={design.title}
                                        className="h-12 w-12 object-cover rounded mr-3"
                                        onError={(e) => {
                                          e.target.src = '/assets/0ImageNotAvailable01.png';
                                        }}
                                      />
                                    )}
                                    <div>
                                      <a
                                        href={`/admin/gallery?designId=${design.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                        }}
                                      >
                                        {design.id}
                                      </a>
                                      <div className="text-sm text-gray-500">{design.title || 'Untitled'}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {majorCategory ? (
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${categoryColor}`}>
                                      {majorCategory}
                                    </span>
                                  ) : (
                                    <span className="text-sm text-gray-400">No category</span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex flex-wrap gap-1">
                                    {minorTags && minorTags.length > 0 ? (
                                      minorTags.map((tag, idx) => (
                                        <span
                                          key={idx}
                                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                                        >
                                          {tag}
                                        </span>
                                      ))
                                    ) : (
                                      <span className="text-sm text-gray-400">No tags</span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  {filteredTagDesigns.length > 0 && (
                    <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-600">
                      Showing {filteredTagDesigns.length} of {bulkTagDesigns.length} design(s)
                      {selectedDesigns.size > 0 && ` • ${selectedDesigns.size} selected`}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Notes Editor Tab */}
        {activeTab === 'notes-editor' && (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            {pageToast && (
              <Toast
                message={pageToast.message}
                type={pageToast.type}
                onClose={() => setPageToast(null)}
              />
            )}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-semibold text-gray-900">
                  Notes Editor
                </h2>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleSyncNotesIndex}
                    disabled={syncNotesIndexLoading || pagesLoading}
                    className="text-sm text-gray-600 hover:text-gray-800 font-medium disabled:opacity-50"
                    title="Sync index with HTML files on disk (use after restore if notes are missing)"
                  >
                    {syncNotesIndexLoading ? 'Syncing...' : 'Sync Index'}
                  </button>
                  <a
                    href="/admin/external-images"
                    className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                  >
                    External Images Manager
                  </a>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Create and manage custom notes. Notes can contain any HTML content and will be displayed with the site header and footer.
              </p>
            </div>

            {pagesError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800">{pagesError}</p>
              </div>
            )}

            {pagesLoading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-4 text-gray-600">Loading notes...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Panel: Pages List */}
                <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Notes</h3>
                    <button
                      onClick={() => {
                        setSelectedPage(null);
                        setPageForm({ title: '', slug: '', content: '', hideTitle: false, hideTags: false, type: 'note', tags: [] });
                        setPageTagsInput('');
                      }}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      New Note
                    </button>
                  </div>

                  {/* Search and Filter Controls */}
                  {pages.length > 0 && (
                    <div className="mb-4 space-y-3">
                      {/* Search Input */}
                      <div>
                        <input
                          type="text"
                          placeholder="Search notes by title or slug..."
                          value={notesSearchQuery}
                          onChange={(e) => setNotesSearchQuery(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* Type Filter, Tag Filter and Sort */}
                      <div className="grid grid-cols-3 gap-2">
                        {/* Type Filter */}
                        <select
                          value={notesTypeFilter}
                          onChange={(e) => setNotesTypeFilter(e.target.value)}
                          className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">All Types</option>
                          <option value="note">Notes</option>
                          <option value="block">Blocks</option>
                        </select>
                        
                        {/* Tag Filter */}
                        <select
                          value={notesTagFilter}
                          onChange={(e) => setNotesTagFilter(e.target.value)}
                          className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">All Tags</option>
                          {Array.from(new Set(pages.flatMap(p => p.tags || []))).sort().map(tag => (
                            <option key={tag} value={tag}>{tag.toLowerCase()}</option>
                          ))}
                        </select>

                        {/* Sort Dropdown */}
                        <select
                          value={notesSortBy}
                          onChange={(e) => setNotesSortBy(e.target.value)}
                          className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="updatedAt">Last Updated</option>
                          <option value="createdAt">Date Created</option>
                          <option value="title">Title (A-Z)</option>
                          <option value="viewCount">Most Views</option>
                        </select>
                      </div>
                    </div>
                  )}
                  
                  {pages.length === 0 ? (
                    <p className="text-gray-500 text-sm py-4">No notes yet. Create your first note!</p>
                  ) : (() => {
                    // Filter and sort pages
                    let filteredPages = [...pages];

                    // Apply search filter
                    if (notesSearchQuery.trim()) {
                      const query = notesSearchQuery.toLowerCase();
                      filteredPages = filteredPages.filter(page =>
                        (page.title || '').toLowerCase().includes(query) ||
                        (page.slug || '').toLowerCase().includes(query) ||
                        (page.name || '').toLowerCase().includes(query)
                      );
                    }

                    // Apply tag filter (case-insensitive)
                    if (notesTagFilter) {
                      const filterTagLower = notesTagFilter.toLowerCase();
                      filteredPages = filteredPages.filter(page =>
                        (page.tags || []).some(tag => tag.toLowerCase() === filterTagLower)
                      );
                    }

                    // Apply sorting
                    filteredPages.sort((a, b) => {
                      switch (notesSortBy) {
                        case 'title':
                          return (a.title || '').localeCompare(b.title || '');
                        case 'createdAt':
                          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
                        case 'updatedAt':
                          return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
                        case 'viewCount':
                          return (b.viewCount || 0) - (a.viewCount || 0);
                        default:
                          return 0;
                      }
                    });

                    return (
                      <>
                        {filteredPages.length === 0 ? (
                          <p className="text-gray-500 text-sm py-4">No notes match your filters.</p>
                        ) : (
                          <div className="space-y-1 max-h-[800px] overflow-y-auto">
                            {filteredPages.map((page) => (
                              <div
                                key={page.name}
                                onClick={() => handleSelectPage(page)}
                                className={`px-2 py-1.5 rounded-lg border cursor-pointer transition-colors ${
                                  selectedPage?.name === page.name
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {/* Line 1: Title, Type Badge, and Views */}
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <h4 className="text-sm font-medium text-gray-900 truncate">{page.title}</h4>
                                    {(page.type || 'note') === 'block' && (
                                      <span className="px-1.5 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded whitespace-nowrap">
                                        Block
                                      </span>
                                    )}
                                    {(page.type || 'note') === 'note' && (
                                      <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded whitespace-nowrap">
                                        Note
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                                    Views: {page.viewCount || 0}
                                  </span>
                                </div>
                                
                                {/* Line 2: Tags and Last Updated */}
                                <div className="flex items-center justify-between">
                                  <div className="flex flex-wrap gap-1 flex-1 min-w-0 pr-2">
                                    {page.tags && page.tags.length > 0 ? (
                                      page.tags.map(tag => (
                                        <span
                                          key={tag}
                                          className="px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs rounded"
                                        >
                                          {tag.toLowerCase()}
                                        </span>
                                      ))
                                    ) : (
                                      <span className="text-xs text-gray-400">No tags</span>
                                    )}
                                  </div>
                                  {page.updatedAt && (
                                    <span className="text-xs text-gray-500 whitespace-nowrap">
                                      Updated: {new Date(page.updatedAt).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {filteredPages.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500 text-center">
                            Showing {filteredPages.length} of {pages.length} note{pages.length !== 1 ? 's' : ''}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Right Panel: Page Editor */}
                <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {selectedPage ? 'Edit Note' : 'Create New Note'}
                    </h3>
                    <div className="flex items-center gap-2">
                      {selectedPage ? (
                        <>
                          <button
                            onClick={handleUpdatePage}
                            disabled={pageSaving || !pageForm.title.trim()}
                            className="px-2 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {pageSaving ? 'Saving...' : 'Update'}
                          </button>
                          <button
                            onClick={handleDuplicatePage}
                            disabled={pageSaving}
                            className="px-2 py-1 text-xs font-medium bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {pageSaving ? '...' : 'Duplicate'}
                          </button>
                          <button
                            onClick={() => setShowDeletePageDialog(true)}
                            disabled={pageDeleting === selectedPage.name}
                            className="px-2 py-1 text-xs font-medium bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {pageDeleting === selectedPage.name ? 'Deleting...' : 'Delete'}
                          </button>
                          <button
                            onClick={handleCancelPageEdit}
                            className="px-2 py-1 text-xs font-medium bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                          >
                            Cancel
                          </button>
                          {selectedPage.slug && (
                            <button
                              onClick={() => window.open(`/note/${selectedPage.slug}`, '_blank')}
                              className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 hover:text-blue-700 transition-colors"
                            >
                              View
                            </button>
                          )}
                          <button
                            onClick={() => setShowRevisionModal(true)}
                            className="px-2 py-1 text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded hover:bg-purple-100 hover:text-purple-700 transition-colors"
                            title="View revision history"
                          >
                            Revisions
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={handleCreatePage}
                          disabled={pageSaving || !pageForm.title.trim()}
                          className="px-2 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {pageSaving ? 'Creating...' : 'Create'}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Note Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={pageForm.title}
                        onChange={(e) => handlePageFormChange('title', e.target.value)}
                        placeholder="Enter note title"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        The note name will be generated from the title (special characters removed, spaces replaced with underscores)
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      <select
                        value={pageForm.type || 'note'}
                        onChange={(e) => handlePageFormChange('type', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="note">Note</option>
                        <option value="block">Block (Reusable)</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        Select "Block" to create a reusable block that can be inserted into notes.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Note Slug (URL)
                      </label>
                      <input
                        type="text"
                        value={pageForm.slug}
                        onChange={(e) => handlePageFormChange('slug', e.target.value)}
                        placeholder="Auto-generated from title"
                        className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 ${
                          slugValidation.checking
                            ? 'border-gray-300 focus:ring-blue-500'
                            : slugValidation.available === false
                            ? 'border-red-300 focus:ring-red-500'
                            : slugValidation.available === true
                            ? 'border-green-300 focus:ring-green-500'
                            : 'border-gray-300 focus:ring-blue-500'
                        }`}
                      />
                      {slugValidation.checking && (
                        <p className="mt-1 text-xs text-gray-500">
                          Checking slug availability...
                        </p>
                      )}
                      {!slugValidation.checking && slugValidation.message && (
                        <p className={`mt-1 text-xs ${
                          slugValidation.available === false
                            ? 'text-red-600'
                            : slugValidation.available === true
                            ? 'text-green-600'
                            : 'text-gray-500'
                        }`}>
                          {slugValidation.message}
                        </p>
                      )}
                      {!slugValidation.checking && !slugValidation.message && (
                        <p className="mt-1 text-xs text-gray-500">
                          URL-friendly identifier for the note. Auto-generated from title but can be customized. Used in the note URL: /note/[slug]
                        </p>
                      )}
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Note Content (HTML)
                        </label>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setShowNoteContentEditorModal(true)}
                            className="text-sm font-medium text-blue-600 hover:text-blue-700 px-2 py-1 rounded hover:bg-blue-50"
                            title="Edit in full-screen editor with preview"
                          >
                            Edit in full screen
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowContentInsertModal(true)}
                            className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-100 transition-colors"
                            title="Insert content from static files"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <textarea
                        ref={pageContentTextareaRef}
                        value={pageForm.content}
                        onChange={(e) => handlePageFormChange('content', e.target.value)}
                        placeholder="Enter HTML content for the note..."
                        rows={15}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        You can use HTML, CSS, and JavaScript. The note will be displayed with the site header and footer.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tags (comma-separated)
                      </label>
                      <input
                        type="text"
                        value={pageTagsInput}
                        onChange={(e) => {
                          setPageTagsInput(e.target.value);
                        }}
                        placeholder="e.g., company, info, about, public"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Enter tags separated by commas. Used for filtering and organizing notes.
                      </p>
                    </div>

                    <div className="flex items-center gap-4">
                      <span className="text-sm font-medium text-gray-700">Hide:</span>
                      <div className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          id="hideTitle"
                          checked={pageForm.hideTitle}
                          onChange={(e) => handlePageFormChange('hideTitle', e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="hideTitle" className="text-sm text-gray-700 cursor-pointer">
                          Title
                        </label>
                      </div>
                      <div className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          id="hideTags"
                          checked={pageForm.hideTags}
                          onChange={(e) => handlePageFormChange('hideTags', e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="hideTags" className="text-sm text-gray-700 cursor-pointer">
                          Tags
                        </label>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            )}

            {showDeletePageDialog && selectedPage && (
              <ConfirmationDialog
                isOpen={showDeletePageDialog}
                onClose={() => setShowDeletePageDialog(false)}
                onConfirm={handleDeletePage}
                title="Delete Note"
                message={`Are you sure you want to delete the note "${selectedPage.title}"? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                confirmClass="bg-red-600 hover:bg-red-700"
              />
            )}

            {showRevisionModal && selectedPage && (
              <RevisionHistoryModal
                isOpen={showRevisionModal}
                onClose={() => setShowRevisionModal(false)}
                noteName={selectedPage.name}
                onRestore={async (restoredNote) => {
                  // Refresh the note data after restore
                  try {
                    const response = await authAxios.get(`${API_BASE_URL}/admin/notes/${selectedPage.name}`);
                    if (response.data.success) {
                      const updatedPage = response.data.data;
                      handleSelectPage(updatedPage);
                      setPageToast({ message: 'Revision restored successfully', type: 'success' });
                    }
                  } catch (err) {
                    console.error('Error refreshing note after restore:', err);
                    setPageToast({ message: 'Revision restored, but failed to refresh note data', type: 'warning' });
                  }
                }}
              />
            )}
          </div>
        )}

        {/* Designs Editor Tab */}
        {activeTab === 'json-editor' && (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            {jsonToast && (
              <Toast
                message={jsonToast.message}
                type={jsonToast.type}
                onClose={() => setJsonToast(null)}
              />
            )}
            
            {/* Quick Links */}
            <div className="mb-6 flex flex-wrap gap-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <a
                  href="/admin/add-design"
                  className="text-blue-700 hover:text-blue-900 font-medium underline flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Design
                </a>
              </div>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <a
                  href="/admin/formats"
                  className="text-blue-700 hover:text-blue-900 font-medium underline flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
                  </svg>
                  Formats
                </a>
              </div>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <button
                  onClick={() => setActiveTab('json-editor')}
                  className="text-blue-700 hover:text-blue-900 font-medium underline flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                  Design Editor
                </button>
              </div>
            </div>
            
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Designs JSON Editor
              </h2>
              <p className="text-sm text-gray-600">
                Edit the full JSON structure of designs. Click on a design to view and edit its complete JSON.
              </p>
            </div>

            {jsonEditorError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800">{jsonEditorError}</p>
              </div>
            )}

            {jsonEditorLoading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-4 text-gray-600">Loading designs...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Panel: Design List */}
                <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
                  <div className="mb-4">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search in full JSON..."
                        value={jsonSearchQuery}
                        onChange={(e) => setJsonSearchQuery(e.target.value)}
                        className="w-full px-4 py-2 pl-10 pr-4 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </div>
                      {jsonSearchQuery && (
                        <button
                          onClick={() => setJsonSearchQuery('')}
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mb-2 text-sm text-gray-600">
                    {jsonSearchQuery.trim()
                      ? `Showing ${filteredJsonDesigns.length} of ${jsonEditorDesigns.length} designs`
                      : `${jsonEditorDesigns.length} design${jsonEditorDesigns.length !== 1 ? 's' : ''} total`}
                  </div>

                  <div className="max-h-[600px] overflow-y-auto">
                    {filteredJsonDesigns.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No designs found</p>
                    ) : (
                      <div className="space-y-2">
                        {filteredJsonDesigns.map((design) => (
                          <button
                            key={design.id}
                            onClick={() => handleSelectJsonDesign(design)}
                            className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                              selectedJsonDesign?.id === design.id
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className="font-mono text-xs text-gray-500 mb-1">{design.id}</div>
                            <div className="font-medium text-gray-900">{design.title || '(No title)'}</div>
                            {design.description && (
                              <div className="text-sm text-gray-600 mt-1 line-clamp-1">
                                {design.description}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Panel: JSON Editor */}
                <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
                  {selectedJsonDesign ? (
                    <div className="h-full flex flex-col">
                      <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          Editing: {selectedJsonDesign.id}
                        </h3>
                        <p className="text-sm text-gray-600">{selectedJsonDesign.title || '(No title)'}</p>
                      </div>

                      {jsonError && (
                        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                          <p className="text-red-800 text-sm">
                            <strong>JSON Error:</strong> {jsonError}
                          </p>
                        </div>
                      )}

                      <div className="flex-1 mb-4">
                        <textarea
                          value={jsonText}
                          onChange={handleJsonChange}
                          className="w-full h-full min-h-[500px] p-4 font-mono text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                          spellCheck={false}
                        />
                      </div>

                      <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                          <button
                            onClick={handleJsonSave}
                            disabled={jsonSaving || !!jsonError}
                            className="flex-1 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {jsonSaving ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={handleJsonDuplicate}
                            disabled={jsonSaving || !!jsonError}
                            className="flex-1 px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {jsonSaving ? 'Duplicating...' : 'Duplicate'}
                          </button>
                          <button
                            onClick={handleJsonCancel}
                            disabled={jsonSaving}
                            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </div>
                        <button
                          onClick={handleJsonOpenInAddForm}
                          disabled={jsonSaving || !!jsonError}
                          className="w-full px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Open with Design Form
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full min-h-[500px] text-gray-500">
                      <div className="text-center">
                        <svg
                          className="w-16 h-16 mx-auto mb-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <p className="text-lg">Select a design to edit its JSON</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content Insert Modal */}
      <ContentInsertModal
        isOpen={showContentInsertModal}
        onClose={() => setShowContentInsertModal(false)}
        onInsert={handleContentInsert}
        textareaRef={pageContentTextareaRef}
      />

      {/* Full-screen note content editor (WYSIWYG + preview) */}
      <NoteContentEditorModal
        isOpen={showNoteContentEditorModal}
        onClose={() => setShowNoteContentEditorModal(false)}
        content={pageForm.content}
        onContentChange={(html) => handlePageFormChange('content', html)}
        onSave={selectedPage ? saveNoteContentFromModal : undefined}
        title={selectedPage?.title || pageForm.title}
      />
    </div>
  );
}

export default SettingsPage;

